import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { useOrganization } from "../OrganizationContext"
import { listTemplates } from "../templates/ga4Template"
import { normalizeValue } from "../utils/normalizeValue"
import { normalizeUrl } from "../utils/security"
import { applyEventOwnersToAccountProducts } from "../utils/productEventOwners"
import { syncStripeSubscription } from "../api/stripeBilling"
import {
  syncGa4AccountForProperty,
  deleteAccountsIfEmpty,
} from "../utils/ga4AccountSync"
import GA4OvernightAuditDialog from "../components/GA4OvernightAuditDialog"

import {
  createCrossDomainCoverageEvaluator,
  formatCrossDomainActionMessage,
  normalizeMeasurementId,
} from "../utils/ga4CrossDomain"
import {
  normalizeUserAccessAuditPayload,
  createUserAccessFingerprint,
  extractTemplateExpectedUsers,
  evaluateUserAccessIssues,
} from "../utils/ga4UserAccess"
import {
  DEFAULT_PROPERTY_TYPE,
  getDefaultTemplateContextKey,
  loadDefaultTemplateMap,
  subscribeToDefaultTemplateChanges,
  ensureDefaultTemplateMap,
  refreshDefaultTemplateMap,
} from "../utils/ga4TemplateDefaults"
import {
  ANALYTICS_MANAGE_USERS_SCOPE,
  getValidAccessToken,
  loadTokens,
  isGa4EditTokenValid,
  tokenHasRequiredAnalyticsScopes,
} from "../utils/ga4Tokens"
import {
  INDUSTRY_CATEGORIES,
  DATA_RETENTION_OPTIONS,
  GOOGLE_SIGNALS_STATES,
} from "./GA4PropertySettings"
import { Button } from "../components/ui"
import useGa4EditAuthorization from "../hooks/useGa4EditAuthorization"

const reconcileDealershipBilling = async (organizationId) => {
  if (!organizationId) return
  try {
    await syncStripeSubscription(organizationId)
  } catch (error) {
    console.error(
      "Dealership changes were saved, but billing reconciliation is pending.",
      error,
    )
  }
}

const FCR_EVENT_NAMES = new Set([
  "asc_form_submission",
  "asc_comm_submission",
  "asc_voice_submission",
])

const PERFORMANCE_COLUMNS = [
  { key: "eventAuditTotal", label: "Total Events (Audit)" },
  { key: "fcrEventCount", label: "FCR Events" },
  { key: "fcrEventOwners", label: "FCR Event Owners" },
  { key: "fcrMonthsTracked", label: "FCR Months" },
  { key: "ccrEventCount", label: "CCR Events" },
  { key: "ccrEventOwners", label: "CCR Event Owners" },
  { key: "ccrProducts", label: "CCR Products" },
  { key: "ccrMonthsTracked", label: "CCR Months" },
]

const formatColumnLabel = (key) =>
  key
    .split(".")
    .map((segment) => {
      const normalized = segment
        .replace(/_/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .trim()
      if (!normalized) return ""
      return normalized.charAt(0).toUpperCase() + normalized.slice(1)
    })
    .join(" › ")

const ADVANCED_AUDIT_FIELD_KEYS = [
  "currencyCode",
  "industryCategory",
  "reportingIdentity",
  "userDataRetention",
  "eventDataRetention",
  "googleSignalsState",
  "resetUserDataOnNewActivity",
]

const ADVANCED_FIELD_CONFIG = {
  currencyCode: { editable: true, type: "currency" },
  industryCategory: { editable: true, type: "industry" },
  reportingIdentity: { editable: false },
  userDataRetention: { editable: true, type: "retention" },
  eventDataRetention: { editable: true, type: "retention" },
  googleSignalsState: { editable: true, type: "googleSignals" },
  resetUserDataOnNewActivity: { editable: true, type: "boolean" },
}

const EXTENDED_AUDIT_CHUNKS = [
  [
    "settings",
    "dataStreams",
    "dataRetentionSettings",
    "googleSignalsSettings",
    "reportingIdentity",
  ],
  ["customDimensions", "customMetrics"],
  [
    "googleAdsLinks",
    "displayVideo360AdvertiserLinks",
    "searchAds360Links",
    "bigQueryLinks",
    "firebaseLinks",
    "attributionSettings",
    "auditUserLinks",
  ],
]
const normalizePropertyTypeKey = (value) => {
  if (value === "PROPERTY_TYPE_360") return "PROPERTY_TYPE_360"
  if (value === "PROPERTY_TYPE_ORDINARY") return "PROPERTY_TYPE_ORDINARY"
  return DEFAULT_PROPERTY_TYPE
}

const POST_AUTH_ACTION_KEY = "ga4_post_auth_action"
const AUTH_ERROR_MSG =
  "Missing or expired GA4 access token. Please add an account via 'Check for New Accounts'."

const DEFAULT_ADVANCED_FORM_VALUES = {
  currencyCode: "",
  industryCategory: "",
  reportingIdentity: "",
  userDataRetention: "",
  eventDataRetention: "",
  googleSignalsState: "",
  resetUserDataOnNewActivity: "",
}

const DAY_IN_MS = 24 * 60 * 60 * 1000

const normalizeAdvancedNumeric = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0
  }
  if (typeof value === "string") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const formatPerformanceCellValue = (value) => {
  if (value === null || value === undefined) return "—"
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString() : "—"
  }
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (Array.isArray(value)) return value.join(", ")
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (err) {
      return String(value)
    }
  }
  return String(value)
}

const extractAuditFieldValue = (settings, key) => {
  if (!settings || typeof settings !== "object") return null
  switch (key) {
    case "reportingIdentity":
      return settings?.reportingIdentity?.reportingIdentity ?? null
    case "userDataRetention":
    case "eventDataRetention":
    case "resetUserDataOnNewActivity":
      return settings?.dataRetentionSettings?.[key] ?? null
    case "googleSignalsState":
      return settings?.googleSignalsSettings?.state ?? null
    default:
      return settings?.[key] ?? null
  }
}

const formatAuditDisplayValue = (value) => {
  if (value === undefined || value === null || value === "") return "—"
  const normalized = normalizeValue(value)
  if (typeof normalized === "boolean") return normalized ? "true" : "false"
  if (typeof normalized === "number") return String(normalized)
  if (typeof normalized === "object") {
    try {
      return JSON.stringify(normalized)
    } catch (err) {
      return String(normalized)
    }
  }
  return String(normalized)
}

const toComparableValue = (value) => {
  try {
    const normalized = normalizeValue(value)
    const serialized = JSON.stringify(normalized)
    if (serialized === undefined) return String(normalized)
    return serialized
  } catch (err) {
    return String(value)
  }
}

const mergeExtendedAuditData = (current, next) => {
  if (!next || typeof next !== "object") return current
  const merged = { ...(current || {}) }
  for (const [key, value] of Object.entries(next)) {
    if (key === "errors" && Array.isArray(value)) {
      const existing = Array.isArray(merged.errors) ? merged.errors : []
      merged.errors = [...existing, ...value]
      continue
    }
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      merged[key] &&
      typeof merged[key] === "object" &&
      !Array.isArray(merged[key])
    ) {
      merged[key] = { ...merged[key], ...value }
      continue
    }
    merged[key] = value
  }
  return merged
}

const extractMeasurementIdsFromSettingsData = (settingsData) => {
  const ids = new Set()
  const addCandidate = (candidate) => {
    const normalized = normalizeMeasurementId(candidate)
    if (normalized) ids.add(normalized)
  }

  const dataStreams = Array.isArray(settingsData?.dataStreams?.dataStreams)
    ? settingsData.dataStreams.dataStreams
    : Array.isArray(settingsData?.dataStreams)
      ? settingsData.dataStreams
      : []

  for (const stream of dataStreams) {
    if (!stream || typeof stream !== "object") continue
    addCandidate(stream.measurementId)
    if (Array.isArray(stream.measurementIds)) {
      for (const candidate of stream.measurementIds) addCandidate(candidate)
    }
    const webData = stream.webStreamData
    if (webData && typeof webData === "object") {
      addCandidate(webData.measurementId)
      if (Array.isArray(webData.measurementIds)) {
        for (const candidate of webData.measurementIds) addCandidate(candidate)
      }
    }
  }

  const visited = new WeakSet()
  const traverse = (node) => {
    if (!node) return
    if (typeof node === "string") {
      addCandidate(node)
      return
    }
    if (typeof node !== "object") return
    if (visited.has(node)) return
    visited.add(node)
    if (Array.isArray(node)) {
      for (const value of node) traverse(value)
      return
    }
    for (const value of Object.values(node)) {
      if (typeof value === "string") addCandidate(value)
      else traverse(value)
    }
  }

  traverse(settingsData)

  return Array.from(ids)
}

const fetchGa4MeasurementScripts = async (measurementIds) => {
  const results = {}
  const uniqueIds = Array.from(
    new Set(
      (Array.isArray(measurementIds) ? measurementIds : [])
        .map((value) => normalizeMeasurementId(value))
        .filter(Boolean),
    ),
  )
  if (uniqueIds.length === 0) return results

  await Promise.all(
    uniqueIds.map(async (measurementId) => {
      try {
        const response = await fetch(
          `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
            measurementId,
          )}`,
        )
        if (!response.ok) {
          const text = await response.text().catch(() => "")
          throw new Error(
            text || `Failed to fetch GA4 measurement script for ${measurementId}`,
          )
        }
        const script = await response.text()
        results[measurementId] = script
      } catch (error) {
        console.error("[GA4] Failed to fetch measurement script", {
          measurementId,
          error,
        })
        results[measurementId] = null
      }
    }),
  )

  return results
}

const sortAvailableProperties = (properties) =>
  properties
    .slice()
    .sort((a, b) => {
      const accountA = (a.accountDisplayName || a.accountLabel || a.account || "").toLowerCase()
      const accountB = (b.accountDisplayName || b.accountLabel || b.account || "").toLowerCase()
      if (accountA !== accountB) return accountA.localeCompare(accountB)
      const nameA = (a.displayName || "").toLowerCase()
      const nameB = (b.displayName || "").toLowerCase()
      if (nameA !== nameB) return nameA.localeCompare(nameB)
      return (a.property || "").toLowerCase().localeCompare((b.property || "").toLowerCase())
    })

export function GA4AuditHeader({
  ga4Enabled,
  advancedMode,
  setAdvancedMode,
  advancedDataset,
  setAdvancedDataset,
  handleAdvancedEditClick,
  tableSearchTerm,
  setTableSearchTerm,
  onManageAccess,
  onViewTemplates,
  automotiveFeaturesEnabled = true,
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
        flexWrap: "wrap",
        marginBottom: "1rem",
      }}
    >
      <h2 style={{ marginBottom: 0 }}>GA4 Audits</h2>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {ga4Enabled && (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={onManageAccess}
            >
              Manage GA4 Access
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onViewTemplates}
            >
              View GA4 Templates
            </Button>
          </>
        )}
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={advancedMode}
            onChange={(e) => setAdvancedMode(e.target.checked)}
            aria-label="Toggle advanced mode"
          />
          Advanced Mode
        </label>
        {advancedMode && (
          <select
            value={advancedDataset}
            onChange={(e) => setAdvancedDataset(e.target.value)}
            aria-label="Choose advanced data view"
          >
            <option value="audits">Audit Settings</option>
            {automotiveFeaturesEnabled && (
              <option value="performance">Automotive Performance</option>
            )}
          </select>
        )}
        {advancedMode && advancedDataset === "audits" && (
          <button
            type="button"
            onClick={handleAdvancedEditClick}
            style={{ marginLeft: "0.75rem" }}
          >
            Edit GA4 Settings
          </button>
        )}
        <input
          type="text"
          value={tableSearchTerm}
          onChange={(e) => setTableSearchTerm(e.target.value)}
          placeholder="Filter by Property ID or Name"
          style={{
            padding: "0.5rem 0.75rem",
            borderRadius: "var(--border-radius)",
            border: "1px solid var(--border-color, #ccc)",
            minWidth: "260px",
          }}
          aria-label="Filter properties by ID or name"
        />
      </div>
    </div>
  )
}

export default function GA4Audit() {
  const user = useUser()
  const { organizations, selectedOrgId, isAutomotive = true } = useOrganization()
  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)
  const isFreeOrg = selectedOrg?.plan?.name === "free"
  const ga4Enabled = selectedOrg?.plan?.ga4_enabled !== false
  const historicalRetentionDays = useMemo(() => {
    const raw = Number(selectedOrg?.plan?.historical_retention_days ?? 0)
    if (!Number.isFinite(raw)) return 0
    return Math.max(0, Math.trunc(raw))
  }, [selectedOrg?.plan?.historical_retention_days])
  const normalizedUserEmail = useMemo(() => {
    if (typeof user?.email !== "string") return null
    const trimmed = user.email.trim()
    if (!trimmed) return null
    return trimmed.toLowerCase()
  }, [user?.email])
  const [orgMemberTokenIndex, setOrgMemberTokenIndex] = useState(() => ({
    orgId: null,
    byEmail: Object.create(null),
  }))
  const getPropertyTokenUserId = useCallback(
    (propertyRecord) => {
      const explicitUserId = (() => {
        if (typeof propertyRecord?.user_id === "string") {
          const value = propertyRecord.user_id.trim()
          if (value) return value
        }
        if (typeof propertyRecord?.token_user_id === "string") {
          const value = propertyRecord.token_user_id.trim()
          if (value) return value
        }
        if (typeof propertyRecord?.linked_user_id === "string") {
          const value = propertyRecord.linked_user_id.trim()
          if (value) return value
        }
        return null
      })()
      if (explicitUserId) return explicitUserId
      const normalizedEmail = (() => {
        if (typeof propertyRecord?.google_account_email !== "string") {
          return ""
        }
        const trimmed = propertyRecord.google_account_email.trim()
        return trimmed ? trimmed.toLowerCase() : ""
      })()
      if (normalizedEmail) {
        const propertyOrgId =
          typeof propertyRecord?.organization_id === "string"
            ? propertyRecord.organization_id
            : null
        const lookupOrgId = orgMemberTokenIndex.orgId
        if (
          lookupOrgId &&
          (propertyOrgId ? propertyOrgId === lookupOrgId : selectedOrgId === lookupOrgId)
        ) {
          const mappedUserId = orgMemberTokenIndex.byEmail[normalizedEmail]
          if (typeof mappedUserId === "string" && mappedUserId) {
            return mappedUserId
          }
        }
      }
      return user?.id || null
    },
    [orgMemberTokenIndex, selectedOrgId, user?.id],
  )
  const pruneHistoricalAuditRows = useCallback(
    async (table, propertyId, keepLimit = 1) => {
      if (!propertyId) return
      if (historicalRetentionDays > 0) {
        try {
          const cutoff = new Date(
            Date.now() - historicalRetentionDays * DAY_IN_MS,
          ).toISOString()
          const { error } = await supabase
            .from(table)
            .delete()
            .eq("ga4_property_id", propertyId)
            .lt("created_at", cutoff)
          if (error) throw error
        } catch (err) {
          console.error(`[GA4] Failed to prune ${table} history`, err)
        }
        return
      }
      try {
        const { data, error } = await supabase
          .from(table)
          .select("id, created_at")
          .eq("ga4_property_id", propertyId)
          .order("created_at", { ascending: false })
        if (error) throw error
        const idsToDelete = (data || [])
          .slice(keepLimit)
          .map((row) => row.id)
          .filter(Boolean)
        if (idsToDelete.length) {
          const { error: deleteError } = await supabase
            .from(table)
            .delete()
            .in("id", idsToDelete)
          if (deleteError) throw deleteError
        }
      } catch (err) {
        console.error(`[GA4] Failed to prune ${table} history`, err)
      }
    },
    [historicalRetentionDays],
  )
  const syncGa4MeasurementScripts = useCallback(
    async ({
      propertyRecord,
      accountId,
      hostnames,
      addedUrls = [],
      measurementIds = [],
      measurementScripts = {},
    }) => {
      const normalizedMeasurementIds = Array.from(
        new Set(
          (Array.isArray(measurementIds) ? measurementIds : [])
            .map((value) => normalizeMeasurementId(value))
            .filter(Boolean),
        ),
      )
      if (normalizedMeasurementIds.length === 0) return {}

      const organizationId =
        propertyRecord?.organization_id ?? selectedOrgId ?? null
      const resolvedUserId = getPropertyTokenUserId(propertyRecord)

      const scriptMap = new Map()
      if (measurementScripts && typeof measurementScripts === "object") {
        for (const measurementId of normalizedMeasurementIds) {
          if (
            Object.prototype.hasOwnProperty.call(
              measurementScripts,
              measurementId,
            )
          ) {
            scriptMap.set(measurementId, measurementScripts[measurementId])
            continue
          }
          const lowerKey = measurementId.toLowerCase()
          if (
            Object.prototype.hasOwnProperty.call(
              measurementScripts,
              lowerKey,
            )
          ) {
            scriptMap.set(measurementId, measurementScripts[lowerKey])
          }
        }
      }

      const missingMeasurementIds = normalizedMeasurementIds.filter(
        (measurementId) => !scriptMap.has(measurementId),
      )
      if (missingMeasurementIds.length) {
        const fetchedScripts = await fetchGa4MeasurementScripts(
          missingMeasurementIds,
        )
        for (const measurementId of missingMeasurementIds) {
          if (Object.prototype.hasOwnProperty.call(fetchedScripts, measurementId)) {
            scriptMap.set(measurementId, fetchedScripts[measurementId] ?? null)
          } else {
            scriptMap.set(measurementId, null)
          }
        }
      }

      const urlSet = new Set()
      if (Array.isArray(addedUrls)) {
        for (const rawUrl of addedUrls) {
          const normalized = normalizeUrl(rawUrl)
          if (normalized) urlSet.add(normalized)
        }
      }
      if (Array.isArray(hostnames)) {
        for (const entry of hostnames) {
          const hostname =
            typeof entry?.hostname === "string" ? entry.hostname : null
          if (!hostname) continue
          const normalized = normalizeUrl(hostname)
          if (normalized) urlSet.add(normalized)
        }
      }

      if (urlSet.size === 0 || !resolvedUserId) {
        return Object.fromEntries(scriptMap.entries())
      }

      let query = supabase.from("website_urls").select("id, url")
      if (accountId) query = query.eq("account_id", accountId)
      if (organizationId) query = query.eq("organization_id", organizationId)
      else query = query.eq("user_id", resolvedUserId)
      query = query.in("url", Array.from(urlSet))

      const { data: websiteRows, error } = await query
      if (error) {
        console.error(
          "[GA4] Failed to load websites for GA4 measurement sync",
          error,
        )
        return Object.fromEntries(scriptMap.entries())
      }
      if (!Array.isArray(websiteRows) || websiteRows.length === 0) {
        return Object.fromEntries(scriptMap.entries())
      }

      const now = new Date().toISOString()
      const ga4InstanceRows = []
      for (const row of websiteRows) {
        if (!row?.id || !row?.url) continue
        for (const measurementId of normalizedMeasurementIds) {
          ga4InstanceRows.push({
            user_id: resolvedUserId,
            website_id: row.id,
            url: row.url,
            ga_id: measurementId,
            found_method: "Synced from GA4 property",
            found_date: now,
            removed_date: null,
          })
        }
      }

      if (ga4InstanceRows.length) {
        const { error: ga4InstancesError } = await supabase
          .from("ga4_instances")
          .upsert(ga4InstanceRows, { onConflict: ["user_id", "url", "ga_id"] })
        if (ga4InstancesError) {
          console.error("[GA4] Failed to upsert GA4 measurement IDs", {
            error: ga4InstancesError,
          })
        }
      }

      const containerRows = []
      for (const row of websiteRows) {
        if (!row?.id || !row?.url) continue
        for (const [measurementId, script] of scriptMap.entries()) {
          if (typeof script !== "string" || !script) continue
          containerRows.push({
            user_id: resolvedUserId,
            organization_id: organizationId,
            website_id: row.id,
            ga_id: measurementId,
            contents: script,
            fetched_at: now,
          })
        }
      }

      if (containerRows.length) {
        const { error: containerError } = await supabase
          .from("ga4_container_contents")
          .upsert(containerRows, {
            onConflict: ["user_id", "website_id", "ga_id"],
          })
        if (containerError) {
          console.error("[GA4] Failed to save GA4 measurement scripts", {
            error: containerError,
          })
        }
      }

      return Object.fromEntries(scriptMap.entries())
    },
    [getPropertyTokenUserId, selectedOrgId],
  )

  const evaluateCrossDomainCoverage = useMemo(
    () =>
      createCrossDomainCoverageEvaluator({
        supabaseClient: supabase,
        selectedOrgId,
        userId: user?.id || null,
      }),
    [selectedOrgId, user?.id],
  )
  const navigate = useNavigate()
  const handleManageAccessClick = useCallback(
    () => navigate("/ga4-access"),
    [navigate],
  )
  const handleViewTemplatesClick = useCallback(
    () => navigate("/ga4-template"),
    [navigate],
  )
  const [savedProperties, setSavedProperties] = useState([])
  const [selectedProps, setSelectedProps] = useState([])
  const [overnightDialogOpen, setOvernightDialogOpen] = useState(false)
  const [auditSaved, setAuditSaved] = useState({})
  const [error, setError] = useState(null)
  const [availableProps, setAvailableProps] = useState([])
  const [showSelectModal, setShowSelectModal] = useState(false)
  const [selectedNewProps, setSelectedNewProps] = useState([])
  const [tableSearchTerm, setTableSearchTerm] = useState("")
  const [modalAccountFilter, setModalAccountFilter] = useState("")
  const [modalPropertyFilter, setModalPropertyFilter] = useState("")
  const [accountOptions, setAccountOptions] = useState([])
  const [accountPickerLoading, setAccountPickerLoading] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState("")
  const [templates, setTemplates] = useState([])
  const [defaultTemplateMap, setDefaultTemplateMap] = useState(() =>
    ensureDefaultTemplateMap()
  )
  const defaultTemplateContextKey = useMemo(
    () =>
      getDefaultTemplateContextKey({
        orgId: selectedOrgId ?? null,
        userId: user?.id ?? null,
      }),
    [selectedOrgId, user?.id]
  )
  const [assignTemplateModalOpen, setAssignTemplateModalOpen] = useState(false)
  const [assignTemplateId, setAssignTemplateId] = useState("")
  const [assignTemplateLoading, setAssignTemplateLoading] = useState(false)
  const [assignTemplateError, setAssignTemplateError] = useState(null)
  const [listingProperties, setListingProperties] = useState(false)
  const [listingProgress, setListingProgress] = useState(null)
  const [lastListingTotals, setLastListingTotals] = useState(null)
  const listingCancelRef = useRef(false)
  const propertyListingCacheRef = useRef(null)
  const oauthWindowRef = useRef(null)
  const silentRefreshAttemptRef = useRef(null)
  const [functionLoading, setFunctionLoading] = useState(null)
  const selectAllCheckboxRef = useRef(null)
  const [advancedMode, setAdvancedMode] = useState(false)
  const [advancedDataset, setAdvancedDataset] = useState("audits")
  const [advancedLoading, setAdvancedLoading] = useState(false)
  const [advancedAuditRaw, setAdvancedAuditRaw] = useState({})
  const [advancedPerformanceRaw, setAdvancedPerformanceRaw] = useState({})
  const [editModalProperty, setEditModalProperty] = useState(null)
  const [editModalLoading, setEditModalLoading] = useState(false)
  const [editModalError, setEditModalError] = useState(null)
  const [editModalSaving, setEditModalSaving] = useState(false)
  const [editModalSettings, setEditModalSettings] = useState(null)
  const [editSelectionError, setEditSelectionError] = useState(null)
  const [editFormValues, setEditFormValues] = useState(() => ({
    ...DEFAULT_ADVANCED_FORM_VALUES,
  }))
  const [advancedReloadNonce, setAdvancedReloadNonce] = useState(0)
  const editModalOpen = Boolean(editModalProperty)

  useEffect(() => {
    if (!isAutomotive && advancedDataset === "performance") {
      setAdvancedDataset("audits")
    }
  }, [advancedDataset, isAutomotive])

  const editAuthorization =
    useGa4EditAuthorization({ supabaseClient: supabase, user }) || {}

  const {
    editTokenRecord,
    editAuthPending,
    editAuthMessage,
    editAuthError,
    editAuthUrl,
    editPopupBlocked,
    resetAuthorizationState: resetEditAuthState,
    refreshTokenFromStorage,
    startEditAuthFlow,
    ensureEditToken,
    handleEditAuthFailure,
  } = editAuthorization

  const currencyOptions = useMemo(
    () =>
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("currency")
        : [],
    []
  )

  const currencySelectOptions = useMemo(() => {
    const options = new Set(currencyOptions)
    if (editFormValues.currencyCode)
      options.add(editFormValues.currencyCode)
    return Array.from(options).sort()
  }, [currencyOptions, editFormValues.currencyCode])

  useEffect(() => {
    setSelectedProps([])
    setSelectedNewProps([])
  }, [selectedOrgId])

  useEffect(() => {
    let isMounted = true
    if (!selectedOrgId) {
      setAccountOptions([])
      setSelectedAccountId("")
      setAccountPickerLoading(false)
      return () => {
        isMounted = false
      }
    }

    setAccountPickerLoading(true)

    supabase
      .from("accounts")
      .select("id, name")
      .eq("organization_id", selectedOrgId)
      .order("name", { ascending: true })
      .then(({ data, error }) => {
        if (!isMounted) return
        if (error) {
          console.error("[GA4] Failed to load organization accounts", error)
          setAccountOptions([])
          setSelectedAccountId("")
          return
        }

        const rows = Array.isArray(data) ? data : []
        setAccountOptions(rows)
        setSelectedAccountId((current) => {
          if (current && rows.some((row) => row.id === current)) {
            return current
          }
          if (rows.length === 1) {
            return rows[0].id
          }
          return ""
        })
      })
      .finally(() => {
        if (isMounted) {
          setAccountPickerLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [selectedOrgId])

  useEffect(() => {
    setSelectedProps((prev) => {
      const validIds = prev.filter((id) =>
        savedProperties.some((property) => property.property_id === id),
      )
      if (
        validIds.length === prev.length &&
        validIds.every((id, index) => id === prev[index])
      ) {
        return prev
      }
      return validIds
    })
  }, [savedProperties])

  useEffect(() => {
    if (!selectedOrgId) {
      if (orgMemberTokenIndex.orgId !== null) {
        setOrgMemberTokenIndex({
          orgId: null,
          byEmail: Object.create(null),
        })
      }
      return
    }
    if (orgMemberTokenIndex.orgId === selectedOrgId) return
    if (typeof supabase?.rpc !== "function") {
      if (orgMemberTokenIndex.orgId !== selectedOrgId) {
        console.warn(
          "[GA4] Supabase RPC client unavailable; skipping organization member token lookup",
        )
        setOrgMemberTokenIndex({
          orgId: selectedOrgId,
          byEmail: Object.create(null),
        })
      }
      return
    }
    let cancelled = false
    const loadOrgMemberTokens = async () => {
      try {
        const { data, error } = await supabase.rpc(
          "get_org_member_emails",
          {
            org_id: selectedOrgId,
          },
        )
        if (cancelled) return
        if (error) {
          console.warn("[GA4] Failed to load organization member tokens", error)
          setOrgMemberTokenIndex({
            orgId: selectedOrgId,
            byEmail: Object.create(null),
          })
          return
        }
        const byEmail = Object.create(null)
        if (Array.isArray(data)) {
          for (const entry of data) {
            const email =
              typeof entry?.email === "string"
                ? entry.email.trim().toLowerCase()
                : ""
            const memberUserId =
              typeof entry?.user_id === "string" && entry.user_id
                ? entry.user_id
                : null
            if (email && memberUserId && !byEmail[email]) {
              byEmail[email] = memberUserId
            }
          }
        }
        setOrgMemberTokenIndex({ orgId: selectedOrgId, byEmail })
      } catch (err) {
        if (cancelled) return
        console.warn("[GA4] Exception loading organization member tokens", err)
        setOrgMemberTokenIndex({
          orgId: selectedOrgId,
          byEmail: Object.create(null),
        })
      }
    }
    loadOrgMemberTokens()
    return () => {
      cancelled = true
    }
  }, [orgMemberTokenIndex.orgId, selectedOrgId])

  useEffect(() => {
    setSelectedNewProps((prev) => {
      const validIds = prev.filter((id) =>
        availableProps.some((property) => property.property === id),
      )
      if (
        validIds.length === prev.length &&
        validIds.every((id, index) => id === prev[index])
      ) {
        return prev
      }
      return validIds
    })
  }, [availableProps])

  useEffect(() => {
    if (typeof window === "undefined") return () => {}

    const handleOAuthMessage = (event) => {
      if (!event?.data || event.data.type !== "ga4-oauth-complete") return
      if (event.origin && event.origin !== window.location.origin) return
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
        oauthWindowRef.current.close()
      }
      oauthWindowRef.current = null
      try {
        localStorage.removeItem(POST_AUTH_ACTION_KEY)
      } catch (err) {
        console.warn("[GA4] Unable to clear post-auth action", err)
      }
      if (typeof window.focus === "function") window.focus()
    }

    window.addEventListener("message", handleOAuthMessage)

    return () => {
      window.removeEventListener("message", handleOAuthMessage)
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
        oauthWindowRef.current.close()
      }
      oauthWindowRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!editModalProperty?.property_id) return
    let cancelled = false
    const loadSettings = async () => {
      setEditModalError(null)
      setEditModalLoading(true)
      const normalizedId = normalizePropertyId(editModalProperty.property_id)
      try {
        let accessToken = null
        try {
          accessToken = await getValidAccessToken(supabase, user)
          if (!accessToken) {
            accessToken = await getValidAccessToken(supabase, user, {
              forceRefresh: true,
            })
          }
        } catch (tokenErr) {
          console.error("[GA4] Failed to resolve existing access token", tokenErr)
        }
        if (!accessToken) {
          if (!cancelled) {
            setEditModalError(AUTH_ERROR_MSG)
            setEditModalLoading(false)
          }
          return
        }
        const { data, error } = await supabase.functions.invoke("ga4-proxy", {
          body: { type: "settings-audit", propertyId: normalizedId },
          headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
        })
        if (cancelled) return
        if (error) {
          throw error
        }
        const payload = data || {}
        const settings = {
          currencyCode: payload.currencyCode || "",
          industryCategory: payload.industryCategory || "",
          reportingIdentity:
            payload.reportingIdentity?.reportingIdentity ||
            payload.settings?.reportingIdentity?.reportingIdentity ||
            "",
          userDataRetention:
            payload.dataRetentionSettings?.userDataRetention || "",
          eventDataRetention:
            payload.dataRetentionSettings?.eventDataRetention || "",
          resetUserDataOnNewActivity:
            payload.dataRetentionSettings?.resetUserDataOnNewActivity ?? "",
          googleSignalsState: payload.googleSignalsSettings?.state || "",
          propertyType:
            payload.propertyType || payload.settings?.propertyType || null,
          reportingIdentitySettings: payload.reportingIdentity || null,
          dataRetentionSettings: payload.dataRetentionSettings || null,
          googleSignalsSettings: payload.googleSignalsSettings || null,
        }
        const resetValue = settings.resetUserDataOnNewActivity
        const resetString =
          resetValue === true
            ? "true"
            : resetValue === false
              ? "false"
              : resetValue === "" || resetValue === null
                ? ""
                : String(resetValue)
        setEditModalSettings(settings)
        setEditFormValues({
          currencyCode: settings.currencyCode || "",
          industryCategory: settings.industryCategory || "",
          reportingIdentity: settings.reportingIdentity || "",
          userDataRetention: settings.userDataRetention || "",
          eventDataRetention: settings.eventDataRetention || "",
          googleSignalsState: settings.googleSignalsState || "",
          resetUserDataOnNewActivity: resetString,
        })
        setEditModalLoading(false)
      } catch (err) {
        console.error("[GA4] Failed to load GA4 settings for edit", err)
        if (!cancelled) {
          const message =
            err?.message ||
            err?.context?.body?.error?.message ||
            "Failed to load GA4 settings."
          setEditModalError(message)
          setEditModalLoading(false)
        }
      }
    }
    loadSettings()
    return () => {
      cancelled = true
    }
  }, [editModalProperty, user])

  const togglePropertySelection = (propertyId) => {
    setSelectedProps((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    )
  }

  const closeEditModal = useCallback(() => {
    resetEditAuthState()
    setEditModalProperty(null)
    setEditModalSettings(null)
    setEditModalLoading(false)
    setEditModalSaving(false)
    setEditModalError(null)
    setEditFormValues({ ...DEFAULT_ADVANCED_FORM_VALUES })
  }, [resetEditAuthState])

  const openAdvancedEditModal = useCallback(
    (property) => {
      if (!property) return
      resetEditAuthState()
      if (user?.id) {
        refreshTokenFromStorage()
      }
      setEditModalProperty(property)
      setEditModalSettings(null)
      setEditModalLoading(true)
      setEditModalSaving(false)
      setEditModalError(null)
      setEditFormValues({ ...DEFAULT_ADVANCED_FORM_VALUES })
    },
    [refreshTokenFromStorage, resetEditAuthState, user?.id]
  )

  useEffect(() => {
    if (!advancedMode || advancedDataset !== "audits") {
      if (editModalOpen) closeEditModal()
    }
  }, [advancedDataset, advancedMode, closeEditModal, editModalOpen])

  const handleEditFormChange = (event) => {
    const { name, value } = event.target
    setEditFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleEditModalSave = useCallback(async (event) => {
    event?.preventDefault()
    if (!editModalProperty?.property_id) return
    setEditModalSaving(true)
    setEditModalError(null)
    const editToken = await ensureEditToken()
    if (!editToken) {
      setEditModalSaving(false)
      return
    }
    const normalizedId = normalizePropertyId(editModalProperty.property_id)
    const propPayload = {
      currencyCode: editFormValues.currencyCode,
      industryCategory: editFormValues.industryCategory,
    }
    const propMask = Object.keys(propPayload).join(",")
    const resetValue =
      editFormValues.resetUserDataOnNewActivity === "true"
        ? true
        : editFormValues.resetUserDataOnNewActivity === "false"
          ? false
          : editFormValues.resetUserDataOnNewActivity
    const drPayload = {
      userDataRetention: editFormValues.userDataRetention,
      eventDataRetention: editFormValues.eventDataRetention,
      resetUserDataOnNewActivity: resetValue,
    }
    const drMask = Object.keys(drPayload).join(",")
    const gsPayload = { state: editFormValues.googleSignalsState }
    const gsMask = Object.keys(gsPayload).join(",")
    const updates = []
    if (propMask) {
      updates.push({
        type: "update-property",
        payload: propPayload,
        updateMask: propMask,
      })
    }
    if (drMask) {
      updates.push({
        type: "update-data-retention",
        payload: drPayload,
        updateMask: drMask,
      })
    }
    if (gsMask) {
      updates.push({
        type: "update-google-signals",
        payload: gsPayload,
        updateMask: gsMask,
      })
    }
    try {
      for (const update of updates) {
        const { error: err } = await supabase.functions.invoke("ga4-proxy", {
          body: { propertyId: normalizedId, ...update },
          headers: { "x-ga4-authorization": `Bearer ${editToken}` },
        })
        if (err) {
          const msg =
            err.context?.body?.error?.message || err.message || "Error"
          const status = err.context?.status
          const normalizedMsg = (msg || "").toLowerCase()
          if (
            status === 401 ||
            status === 403 ||
            normalizedMsg.includes("permission") ||
            normalizedMsg.includes("insufficient")
          ) {
            await handleEditAuthFailure(msg)
          }
          throw new Error(msg)
        }
      }
      setAdvancedAuditRaw((prev) => {
        if (!editModalProperty?.property_id) return prev
        const propertyId = editModalProperty.property_id
        const next = { ...(prev || {}) }
        const existing = next[propertyId] || {}
        const existingSettings = existing.settings || {}
        const nextDataRetention = {
          ...(existingSettings.dataRetentionSettings || {}),
          userDataRetention: editFormValues.userDataRetention,
          eventDataRetention: editFormValues.eventDataRetention,
          resetUserDataOnNewActivity: resetValue,
        }
        const nextSettings = {
          ...existingSettings,
          currencyCode: editFormValues.currencyCode,
          industryCategory: editFormValues.industryCategory,
          dataRetentionSettings: nextDataRetention,
          googleSignalsSettings: {
            ...(existingSettings.googleSignalsSettings || {}),
            state: editFormValues.googleSignalsState,
          },
        }
        next[propertyId] = {
          ...existing,
          settings: nextSettings,
        }
        return next
      })
      setAdvancedReloadNonce((prev) => prev + 1)
      setEditModalSaving(false)
      closeEditModal()
    } catch (err) {
      const msg =
        err?.message ||
        err?.context?.body?.error?.message ||
        "Failed to update GA4 settings."
      setEditModalError(msg)
      setEditModalSaving(false)
    }
  }, [
    closeEditModal,
    editFormValues.currencyCode,
    editFormValues.eventDataRetention,
    editFormValues.googleSignalsState,
    editFormValues.industryCategory,
    editFormValues.resetUserDataOnNewActivity,
    editFormValues.userDataRetention,
    editModalProperty?.property_id,
    ensureEditToken,
    handleEditAuthFailure,
  ])

  const renderEditFieldInput = (key, disabled = false) => {
    const value = editFormValues[key] ?? ""
    switch (key) {
      case "currencyCode":
        return currencySelectOptions.length ? (
          <select
            name="currencyCode"
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          >
            <option value="">Select a currency</option>
            {currencySelectOptions.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        ) : (
          <input
            name="currencyCode"
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          />
        )
      case "industryCategory":
        return (
          <select
            name="industryCategory"
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          >
            <option value="">Select an industry</option>
            {INDUSTRY_CATEGORIES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
      case "userDataRetention":
      case "eventDataRetention":
        return (
          <select
            name={key}
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          >
            <option value="">Select a duration</option>
            {DATA_RETENTION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
      case "googleSignalsState":
        return (
          <select
            name="googleSignalsState"
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          >
            <option value="">Select a state</option>
            {GOOGLE_SIGNALS_STATES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )
      case "resetUserDataOnNewActivity":
        return (
          <select
            name="resetUserDataOnNewActivity"
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          >
            <option value="">Select an option</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        )
      default:
        return (
          <input
            name={key}
            value={value}
            onChange={handleEditFormChange}
            disabled={disabled}
          />
        )
    }
  }

  
  const auditSteps = [
    { key: "settings", label: "Collecting property settings" },
    { key: "userAccess", label: "Reviewing user access permissions" },
    { key: "keyEvents", label: "Retrieving key events" },
    ...(isAutomotive
      ? [{ key: "eventAudit", label: "Analyzing automotive event diagnostics" }]
      : []),
    { key: "pageLocationAudit", label: "Scanning page locations for PII" },
    ...(isAutomotive
      ? [{ key: "ccrReport", label: "Gathering CCR interactions" }]
      : []),
    { key: "hostnameAudit", label: "Reviewing hostnames" },
    { key: "channelAudit", label: "Reviewing channel groupings" },
    { key: "selfReferralAudit", label: "Checking for self-referrals" },
    { key: "googleAds", label: "Reviewing Google Ads links" },
  ]

  const normalizePropertyId = (pid) => pid.replace(/^properties\//, "")

  const compareWithTemplate = (template, settings, events) => {
    const report = { settings: {} }
    if (!template) return report

    const pt = settings?.propertyType
    const tSettings =
      template.propertyTypes?.[pt] ||
      template.propertyTypes?.PROPERTY_TYPE_ORDINARY ||
      {}
    const getActual = (key) => {
      if (key === "reportingIdentity")
        return settings?.reportingIdentity?.reportingIdentity
      if (
        [
          "userDataRetention",
          "eventDataRetention",
          "resetUserDataOnNewActivity",
        ].includes(key)
      )
        return settings?.dataRetentionSettings?.[key]
      if (key === "googleSignalsState")
        return settings?.googleSignalsSettings?.state
      return settings?.[key]
    }
    for (const [key, cfg] of Object.entries(tSettings)) {
      if (cfg.action === "ignore") continue
      const actual = getActual(key)
      const expected = cfg.value
      const match =
        JSON.stringify(normalizeValue(actual)) ===
        JSON.stringify(normalizeValue(expected))
      report.settings[key] = { actual, expected, match }
    }

    const toKey = (obj) =>
      JSON.stringify(normalizeValue(obj), Object.keys(obj).sort())

    const defMode = template.customDefinitionsMode || "include"
    const sanitizeDefinition = (definition, fallbackType = "dimension") => {
      if (!definition || typeof definition !== "object") return null
      const { disallowAdsPersonalization, ...rest } = definition
      const type =
        rest.type === "metric"
          ? "metric"
          : rest.type === "dimension"
          ? "dimension"
          : fallbackType === "metric"
          ? "metric"
          : "dimension"
      const parameterName =
        typeof rest.parameterName === "string"
          ? rest.parameterName.trim()
          : ""
      const displayName =
        typeof rest.displayName === "string" ? rest.displayName.trim() : ""
      return {
        ...rest,
        type,
        parameterName,
        displayName,
      }
    }

    const toDefKey = (definition) => {
      if (!definition || typeof definition !== "object") return null
      const type = definition.type === "metric" ? "metric" : "dimension"
      const parameterName =
        typeof definition.parameterName === "string"
          ? definition.parameterName
          : ""
      if (!parameterName) return null
      return `${type}::${parameterName}`
    }

    const expectedDefs = (template.customDefinitions || [])
      .map((def) => sanitizeDefinition(def, def?.type))
      .filter(Boolean)
    const actualDefs = [
      ...((settings?.customDimensions?.customDimensions || [])
        .map((d) =>
          sanitizeDefinition(
            {
              type: "dimension",
              parameterName: d.parameterName,
              displayName: d.displayName,
              description: d.description,
              scope: d.scope,
            },
            "dimension"
          )
        )
        .filter(Boolean) || []),
      ...((settings?.customMetrics?.customMetrics || [])
        .map((m) =>
          sanitizeDefinition(
            {
              type: "metric",
              parameterName: m.parameterName,
              displayName: m.displayName,
              description: m.description,
              measurementUnit: m.measurementUnit,
              restrictedMetricType: m.restrictedMetricType,
            },
            "metric"
          )
        )
        .filter(Boolean) || []),
    ]

    const expectedMap = new Map()
    for (const def of expectedDefs) {
      const key = toDefKey(def)
      if (key) expectedMap.set(key, def)
    }
    const actualMap = new Map()
    for (const def of actualDefs) {
      const key = toDefKey(def)
      if (key) actualMap.set(key, def)
    }

    const missingDefs = []
    for (const [key, def] of expectedMap.entries()) {
      if (!actualMap.has(key)) missingDefs.push(def)
    }
    if (missingDefs.length) report.customDefinitionsMissing = missingDefs

    if (defMode === "match") {
      const extraDefs = []
      for (const [key, def] of actualMap.entries()) {
        if (!expectedMap.has(key)) extraDefs.push(def)
      }
      if (extraDefs.length) report.customDefinitionsExtra = extraDefs
    }

    const displayNameMismatches = []
    for (const [key, expectedDef] of expectedMap.entries()) {
      const actualDef = actualMap.get(key)
      if (!actualDef) continue
      const expectedName = expectedDef.displayName || ""
      const actualName = actualDef.displayName || ""
      if (expectedName && expectedName !== actualName) {
        displayNameMismatches.push({
          type: expectedDef.type,
          parameterName: expectedDef.parameterName,
          expectedDisplayName: expectedName,
          actualDisplayName: actualName,
        })
      }
    }
    if (displayNameMismatches.length) {
      report.customDefinitionsDisplayNameMismatch = displayNameMismatches
    }

    const keyMode = template.keyEventsMode || "include"
    const expectedEvents = template.keyEvents || []
    const actualEvents = (events?.conversionEvents || []).map((e) => ({
      eventName: e.eventName,
      countingMethod: e.countingMethod,
      defaultValue: e.defaultValue || {},
    }))
    const actualEvtSet = new Set(actualEvents.map(toKey))
    const expectedEvtSet = new Set(expectedEvents.map(toKey))
    const missingEvts = [...expectedEvtSet]
      .filter((k) => !actualEvtSet.has(k))
      .map((k) => JSON.parse(k))
    if (missingEvts.length) report.keyEventsMissing = missingEvts
    if (keyMode === "match") {
      const extraEvts = [...actualEvtSet]
        .filter((k) => !expectedEvtSet.has(k))
        .map((k) => JSON.parse(k))
      if (extraEvts.length) report.keyEventsExtra = extraEvts
    }

    const MATCH_TYPE_SET = new Set([
      "MATCH_TYPE_UNSPECIFIED",
      "EXACT",
      "BEGINS_WITH",
      "ENDS_WITH",
      "CONTAINS",
      "FULL_REGEXP",
      "PARTIAL_REGEXP",
    ])

    const PARAMETER_OPERATION_SET = new Set(["SET_VALUE", "COPY_PARAMETER"])
    const PARAMETER_VALUE_TYPE_SET = new Set(["STRING", "INT", "DOUBLE", "BOOLEAN"])

    const deriveSourceEventFromConditions = (conditions) => {
      if (!Array.isArray(conditions)) return ""
      for (const condition of conditions) {
        const param =
          typeof condition?.eventParameter === "string"
            ? condition.eventParameter
            : typeof condition?.parameter === "string"
            ? condition.parameter
            : ""
        if (param !== "event_name") continue
        const matchType =
          typeof condition?.matchType === "string"
            ? condition.matchType.toUpperCase()
            : typeof condition?.operator === "string"
            ? condition.operator.toUpperCase()
            : ""
        if (matchType !== "EXACT") continue
        const value = typeof condition?.value === "string" ? condition.value : ""
        if (!value) continue
        return value
      }
      return ""
    }

    const sanitizeMatchingCondition = (condition) => {
      const normalized = normalizeValue(condition)
      if (!normalized || typeof normalized !== "object") return null
      const eventParameter =
        typeof normalized.eventParameter === "string"
          ? normalized.eventParameter
          : typeof normalized.parameter === "string"
          ? normalized.parameter
          : ""
      if (!eventParameter) return null
      let matchType =
        typeof normalized.matchType === "string"
          ? normalized.matchType.toUpperCase()
          : typeof normalized.operator === "string"
          ? normalized.operator.toUpperCase()
          : "MATCH_TYPE_UNSPECIFIED"
      if (!MATCH_TYPE_SET.has(matchType)) matchType = "MATCH_TYPE_UNSPECIFIED"
      const caseSensitive =
        typeof normalized.caseSensitive === "boolean"
          ? normalized.caseSensitive
          : typeof normalized.ignoreCase === "boolean"
          ? !normalized.ignoreCase
          : undefined
      const value =
        typeof normalized.value === "string"
          ? normalized.value
          : typeof normalized.comparisonValue === "string"
          ? normalized.comparisonValue
          : ""
      const negated = normalized.negated === true
      const entry = { eventParameter, matchType }
      if (value) entry.value = value
      if (caseSensitive !== undefined) entry.caseSensitive = caseSensitive
      if (negated) entry.negated = true
      return entry
    }

    const sanitizeParameterMutation = (mutation) => {
      const normalized = normalizeValue(mutation)
      if (!normalized || typeof normalized !== "object") return null
      const parameter =
        typeof normalized.parameter === "string" ? normalized.parameter : ""
      if (!parameter) return null
      let operation =
        typeof normalized.operation === "string"
          ? normalized.operation.toUpperCase()
          : ""
      if (!PARAMETER_OPERATION_SET.has(operation)) {
        operation = normalized.copyFromParameter ? "COPY_PARAMETER" : "SET_VALUE"
      }
      if (operation === "COPY_PARAMETER") {
        const copyFrom =
          typeof normalized.copyFromParameter === "string"
            ? normalized.copyFromParameter
            : typeof normalized.sourceParameter === "string"
            ? normalized.sourceParameter
            : ""
        const entry = { parameter, operation }
        if (copyFrom) entry.copyFromParameter = copyFrom
        return entry
      }
      let valueType =
        typeof normalized.valueType === "string"
          ? normalized.valueType.toUpperCase()
          : ""
      let value = normalized.value
      const parameterValue = normalizeValue(normalized.parameterValue)
      if (parameterValue && typeof parameterValue === "object") {
        if (parameterValue.stringValue != null) {
          valueType = "STRING"
          value = parameterValue.stringValue
        } else if (parameterValue.doubleValue != null) {
          valueType = "DOUBLE"
          value = parameterValue.doubleValue
        } else if (parameterValue.intValue != null) {
          valueType = "INT"
          value = parameterValue.intValue
        } else if (typeof parameterValue.boolValue === "boolean") {
          valueType = "BOOLEAN"
          value = parameterValue.boolValue ? "true" : "false"
        }
      }
      if (!PARAMETER_VALUE_TYPE_SET.has(valueType)) valueType = "STRING"
      if (value == null) value = ""
      if (typeof value !== "string") value = String(value)
      return { parameter, operation: "SET_VALUE", valueType, value }
    }

    const sortByStableKey = (items) =>
      items
        .slice()
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))

    const sanitizeEventCreateRule = (rule) => {
      const normalized = normalizeValue(rule)
      if (!normalized || typeof normalized !== "object") return null
      const sourceEvent =
        typeof normalized.sourceEvent === "string"
          ? normalized.sourceEvent
          : ""
      const destinationEvent =
        typeof normalized.destinationEvent === "string"
          ? normalized.destinationEvent
          : ""
      if (!destinationEvent) return null
      const matchingConditions = Array.isArray(normalized.matchingConditions)
        ? normalized.matchingConditions
            .map(sanitizeMatchingCondition)
            .filter(Boolean)
        : []
      const parameterMutations = Array.isArray(normalized.parameterMutations)
        ? normalized.parameterMutations
            .map(sanitizeParameterMutation)
            .filter(Boolean)
        : []
      const description =
        typeof normalized.description === "string"
          ? normalized.description
          : ""
      const disabled =
        typeof normalized.disabled === "boolean"
          ? normalized.disabled
          : undefined
      const copyParameters =
        typeof normalized.copyParameters === "boolean"
          ? normalized.copyParameters
          : undefined
      const entry = {
        destinationEvent,
      }
      if (sourceEvent) entry.sourceEvent = sourceEvent
      else if (!sourceEvent && matchingConditions.length) {
        const derived = deriveSourceEventFromConditions(matchingConditions)
        if (derived) entry.sourceEvent = derived
      }
      if (description) entry.description = description
      if (disabled === true) entry.disabled = true
      if (copyParameters === true) entry.copyParameters = true
      if (matchingConditions.length) {
        entry.matchingConditions = sortByStableKey(matchingConditions)
      }
      if (parameterMutations.length) {
        entry.parameterMutations = sortByStableKey(parameterMutations)
      }
      return entry
    }

    const sanitizeEventEditRule = (rule) => {
      const normalized = normalizeValue(rule)
      if (!normalized || typeof normalized !== "object") return null
      const eventName =
        typeof normalized.eventName === "string" ? normalized.eventName : ""
      if (!eventName) return null
      const matchingConditions = Array.isArray(normalized.matchingConditions)
        ? normalized.matchingConditions
            .map(sanitizeMatchingCondition)
            .filter(Boolean)
        : []
      const parameterMutations = Array.isArray(normalized.parameterMutations)
        ? normalized.parameterMutations
            .map(sanitizeParameterMutation)
            .filter(Boolean)
        : []
      const description =
        typeof normalized.description === "string"
          ? normalized.description
          : ""
      const disabled =
        typeof normalized.disabled === "boolean"
          ? normalized.disabled
          : undefined
      const entry = {
        eventName,
      }
      if (description) entry.description = description
      if (disabled === true) entry.disabled = true
      if (matchingConditions.length) {
        entry.matchingConditions = sortByStableKey(matchingConditions)
      }
      if (parameterMutations.length) {
        entry.parameterMutations = sortByStableKey(parameterMutations)
      }
      return entry
    }

    const toEventRuleKey = (rule) => {
      if (!rule || typeof rule !== "object") return ""
      const normalized = normalizeValue(rule)
      if (!normalized || typeof normalized !== "object") return ""
      if (Array.isArray(normalized.matchingConditions)) {
        normalized.matchingConditions = sortByStableKey(
          normalized.matchingConditions.map((item) => normalizeValue(item)).filter(Boolean)
        )
      }
      if (Array.isArray(normalized.parameterMutations)) {
        normalized.parameterMutations = sortByStableKey(
          normalized.parameterMutations.map((item) => normalizeValue(item)).filter(Boolean)
        )
      }
      const sortedKeys = Object.keys(normalized).sort()
      return JSON.stringify(normalized, sortedKeys)
    }

    const createMode = template.eventCreateRulesMode || "include"
    const expectedCreateRules = Array.isArray(template.eventCreateRules)
      ? template.eventCreateRules.map(sanitizeEventCreateRule).filter(Boolean)
      : []
    const actualCreateRules = Array.isArray(events?.eventCreateRules)
      ? events.eventCreateRules.map(sanitizeEventCreateRule).filter(Boolean)
      : []
    const expectedCreateMap = new Map()
    for (const rule of expectedCreateRules) {
      expectedCreateMap.set(toEventRuleKey(rule), rule)
    }
    const actualCreateMap = new Map()
    for (const rule of actualCreateRules) {
      actualCreateMap.set(toEventRuleKey(rule), rule)
    }
    const missingCreate = []
    for (const [key, rule] of expectedCreateMap.entries()) {
      if (!actualCreateMap.has(key)) missingCreate.push(rule)
    }
    if (missingCreate.length) report.eventCreateRulesMissing = missingCreate
    if (createMode === "match") {
      const extraCreate = []
      for (const [key, rule] of actualCreateMap.entries()) {
        if (!expectedCreateMap.has(key)) extraCreate.push(rule)
      }
      if (extraCreate.length) report.eventCreateRulesExtra = extraCreate
    }

    const editMode = template.eventEditRulesMode || "include"
    const expectedEditRules = Array.isArray(template.eventEditRules)
      ? template.eventEditRules.map(sanitizeEventEditRule).filter(Boolean)
      : []
    const actualEditRules = Array.isArray(events?.eventEditRules)
      ? events.eventEditRules.map(sanitizeEventEditRule).filter(Boolean)
      : []
    const expectedEditMap = new Map()
    for (const rule of expectedEditRules) {
      expectedEditMap.set(toEventRuleKey(rule), rule)
    }
    const actualEditMap = new Map()
    for (const rule of actualEditRules) {
      actualEditMap.set(toEventRuleKey(rule), rule)
    }
    const missingEdit = []
    for (const [key, rule] of expectedEditMap.entries()) {
      if (!actualEditMap.has(key)) missingEdit.push(rule)
    }
    if (missingEdit.length) report.eventEditRulesMissing = missingEdit
    if (editMode === "match") {
      const extraEdit = []
      for (const [key, rule] of actualEditMap.entries()) {
        if (!expectedEditMap.has(key)) extraEdit.push(rule)
      }
      if (extraEdit.length) report.eventEditRulesExtra = extraEdit
    }

    const extractBigQueryLinks = (value) => {
      if (Array.isArray(value)) return value
      if (value && typeof value === "object") {
        if (Array.isArray(value.bigQueryLinks)) return value.bigQueryLinks
      }
      return []
    }

    const normalizeLinkBoolean = (value) => {
      if (value === true || value === false) return value
      if (typeof value === "string") {
        const trimmed = value.trim().toLowerCase()
        if (trimmed === "true") return true
        if (trimmed === "false") return false
      }
      return undefined
    }

    const sanitizeActualBigQueryLink = (entry) => {
      if (!entry || typeof entry !== "object") return null
      const project =
        typeof entry.project === "string" ? entry.project.trim() : ""
      if (!project) return null
      const normalized = { project }
      const daily = normalizeLinkBoolean(entry.dailyExportEnabled)
      if (daily !== undefined) normalized.dailyExportEnabled = daily
      const streaming = normalizeLinkBoolean(entry.streamingExportEnabled)
      if (streaming !== undefined) normalized.streamingExportEnabled = streaming
      const fresh = normalizeLinkBoolean(entry.freshDailyExportEnabled)
      if (fresh !== undefined) normalized.freshDailyExportEnabled = fresh
      const includeAds = normalizeLinkBoolean(entry.includeAdvertisingId)
      if (includeAds !== undefined) normalized.includeAdvertisingId = includeAds
      return normalized
    }

    const sanitizeExpectedBigQueryLink = (entry) => {
      if (!entry || typeof entry !== "object") return null
      const project =
        typeof entry.project === "string" ? entry.project.trim() : ""
      if (!project) return null
      const normalized = { project }
      const daily = normalizeLinkBoolean(entry.dailyExportEnabled)
      if (daily !== undefined) normalized.dailyExportEnabled = daily
      const streaming = normalizeLinkBoolean(entry.streamingExportEnabled)
      if (streaming !== undefined) normalized.streamingExportEnabled = streaming
      const fresh = normalizeLinkBoolean(entry.freshDailyExportEnabled)
      if (fresh !== undefined) normalized.freshDailyExportEnabled = fresh
      const includeAds = normalizeLinkBoolean(entry.includeAdvertisingId)
      if (includeAds !== undefined) normalized.includeAdvertisingId = includeAds
      return normalized
    }

    const toBigQueryLinkKey = (entry) => {
      if (!entry || typeof entry !== "object") return null
      const project =
        typeof entry.project === "string" ? entry.project.toLowerCase() : ""
      return project || null
    }

    const bigQueryAction =
      typeof template.bigQueryLinksAction === "string"
        ? template.bigQueryLinksAction
        : "dont-manage"
    if (bigQueryAction !== "manage") {
      return report
    }

    const expectedBigQueryLinks = Array.isArray(template.bigQueryLinks)
      ? template.bigQueryLinks
          .map((link) => sanitizeExpectedBigQueryLink(link))
          .filter(Boolean)
      : []
    const actualBigQueryLinks = extractBigQueryLinks(settings?.bigQueryLinks)
      .map((link) => sanitizeActualBigQueryLink(link))
      .filter(Boolean)
    const expectedBigQueryMap = new Map()
    for (const link of expectedBigQueryLinks) {
      const key = toBigQueryLinkKey(link)
      if (!key) continue
      expectedBigQueryMap.set(key, link)
    }
    const actualBigQueryMap = new Map()
    for (const link of actualBigQueryLinks) {
      const key = toBigQueryLinkKey(link)
      if (!key) continue
      if (!actualBigQueryMap.has(key)) actualBigQueryMap.set(key, link)
    }

    const missingBigQueryLinks = []
    const mismatchedBigQueryLinks = []
    for (const [key, expectedLink] of expectedBigQueryMap.entries()) {
      const actualLink = actualBigQueryMap.get(key) || null
      if (!actualLink) {
        missingBigQueryLinks.push({
          project: expectedLink.project || "",
          expected: expectedLink,
        })
        continue
      }
      const mismatchedFields = {}
      const compareBooleanField = (field) => {
        if (
          expectedLink[field] === true ||
          expectedLink[field] === false
        ) {
          const actualValue =
            actualLink[field] === true
              ? true
              : actualLink[field] === false
              ? false
              : undefined
          if (actualValue !== expectedLink[field]) {
            mismatchedFields[field] = {
              expected: expectedLink[field],
              actual: actualValue,
            }
          }
        }
      }
      compareBooleanField("dailyExportEnabled")
      compareBooleanField("streamingExportEnabled")
      compareBooleanField("freshDailyExportEnabled")
      compareBooleanField("includeAdvertisingId")
      if (Object.keys(mismatchedFields).length) {
        mismatchedBigQueryLinks.push({
          project: expectedLink.project || actualLink.project || "",
          expected: expectedLink,
          actual: actualLink,
          mismatchedFields,
        })
      }
    }

    if (missingBigQueryLinks.length)
      report.bigQueryLinksMissing = missingBigQueryLinks
    if (mismatchedBigQueryLinks.length)
      report.bigQueryLinksMismatch = mismatchedBigQueryLinks

    return report
  }

  const looksLikeUri = (value) => {
    if (typeof value !== "string") return false
    const trimmed = value.trim()
    if (!trimmed) return false
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return true
    if (/^www\./i.test(trimmed)) return true
    return false
  }

  const countSettingsAuditIssues = (settingsData) => {
    if (!settingsData || typeof settingsData !== "object") return 0
    let count = 0
    const visited = new WeakSet()
    const visit = (node) => {
      if (!node || typeof node !== "object") return
      if (visited.has(node)) return
      visited.add(node)
      if (Array.isArray(node)) {
        node.forEach(visit)
        return
      }
      for (const [key, value] of Object.entries(node)) {
        if (key === "errors" && Array.isArray(value)) {
          count += value.length
          continue
        }
        if (
          (key === "match" || key === "matches") &&
          (value === false ||
            (typeof value === "string" && value.toLowerCase() === "no"))
        ) {
          count += 1
          continue
        }
        if (key === "displayName" && looksLikeUri(value)) {
          count += 1
          continue
        }
        if (value && typeof value === "object") visit(value)
      }
    }
    visit(settingsData)
    return count
  }

  const countTemplateMismatchIssues = (comparison) => {
    if (!comparison || typeof comparison !== "object") return 0
    let total = 0
    const settingsReport = comparison.settings
    if (settingsReport && typeof settingsReport === "object") {
      for (const entry of Object.values(settingsReport)) {
        if (!entry || typeof entry !== "object") continue
        const matchValue = entry.match
        if (
          matchValue === false ||
          (typeof matchValue === "string" && matchValue.toLowerCase() === "no")
        ) {
          total += 1
        }
      }
    }
    const listKeys = [
      "customDefinitionsMissing",
      "customDefinitionsExtra",
      "keyEventsMissing",
      "keyEventsExtra",
      "eventCreateRulesMissing",
      "eventCreateRulesExtra",
      "eventEditRulesMissing",
      "eventEditRulesExtra",
      "bigQueryLinksMissing",
      "bigQueryLinksExtra",
      "bigQueryLinksMismatch",
    ]
    for (const key of listKeys) {
      const list = comparison[key]
      if (Array.isArray(list)) total += list.length
    }
    return total
  }

  const getToken = async (options = {}) =>
    getValidAccessToken(supabase, user, options)

  const ensureAccessToken = async (postAuthAction) => {
    try {
      const token = await getToken()
      if (token) return token
      const refreshed = await getToken({ forceRefresh: true })
      if (refreshed) return refreshed
    } catch (err) {
      console.error(
        "[GA4] Failed to resolve existing access token",
        err
      )
    }
    await refreshGoogleAuth(undefined, postAuthAction)
    return null
  }

  const refreshGoogleAuth = async (
    scopes =
      "https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/analytics.manage.users.readonly",
    postAuthAction,
    options = {},
  ) => {
    const forcePrompt =
      options?.forcePrompt === true || options?.forcePrompt === "consent"
    console.log("[GA4] Refreshing Google OAuth flow")
    if (postAuthAction) {
      try {
        localStorage.setItem(POST_AUTH_ACTION_KEY, postAuthAction)
      } catch (e) {
        console.warn("[GA4] Unable to store post-auth action", e)
      }
    }
    try {
      let queryParams = { access_type: isFreeOrg ? "online" : "offline" }
      const { data: sessionData } = await supabase.auth.getSession()
      const session = sessionData?.session
      let storedTokens = {}
      if (user) {
        try {
          storedTokens = (await loadTokens(supabase, user.id)) || {}
        } catch (tokenErr) {
          console.warn(
            "[GA4] Unable to load stored GA4 tokens before OAuth",
            tokenErr,
          )
          storedTokens = {}
        }
      }

      const storedRefreshTokenPresent = Boolean(
        storedTokens &&
          Object.prototype.hasOwnProperty.call(storedTokens, "refresh_token") &&
          storedTokens.refresh_token,
      )
      if (!isFreeOrg && !session?.provider_refresh_token && user) {
        if (!storedRefreshTokenPresent) {
          queryParams.prompt = "consent"
        }
      }

      const storedAccessToken =
        storedTokens && typeof storedTokens.access_token === "string"
          ? storedTokens.access_token
          : null
      const hadStoredTokens = Boolean(
        storedAccessToken || storedRefreshTokenPresent,
      )

      let shouldForceConsent = forcePrompt
      if (!shouldForceConsent && hadStoredTokens) {
        const hasScope = storedAccessToken
          ? await tokenHasRequiredAnalyticsScopes(
              storedAccessToken,
              ANALYTICS_MANAGE_USERS_SCOPE,
            )
          : false
        if (!hasScope) {
          shouldForceConsent = true
          console.log(
            "[GA4] Requesting OAuth consent to ensure manage users scope",
            { userId: user?.id || null },
          )
        }
      }

      if (shouldForceConsent) {
        queryParams.prompt = "consent"
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          scopes,
          redirectTo: window.location.href,
          skipBrowserRedirect: true,
          queryParams,
        },
      })
      console.log("[GA4] OAuth response", data, error)
      if (error) throw error
      if (data?.url) {
        const popup = window.open(
          data.url,
          "ga4oauth",
          "width=500,height=600",
        )
        if (popup) {
          oauthWindowRef.current = popup
          popup.focus?.()
        } else {
          console.warn("[GA4] Unable to open OAuth popup window")
        }
      }
    } catch (err) {
      console.error("[GA4] OAuth error", err)
      setError(err.message)
    }
  }

  const invoke = async (payload, options = {}) => {
    const {
      skipLoading = false,
      loadingMessage = "Contacting GA4 proxy…",
      loadingDetails = "",
      progress = null,
      tokenUserId = null,
    } = options

    const shouldRetryForError = (error) => {
      if (!error) return false
      const message = typeof error.message === "string" ? error.message : ""
      if (message.includes("Missing Google access token")) return true
      const status = error.context?.status || error.status || error.code
      return status === 401
    }

    const shouldRetryForApiError = (apiError) => {
      if (!apiError) return false
      const code =
        typeof apiError.code === "number"
          ? apiError.code
          : parseInt(apiError.code ?? "", 10) || null
      const statusText =
        typeof apiError.status === "string"
          ? apiError.status.toUpperCase()
          : ""
      return code === 401 || statusText === "UNAUTHENTICATED"
    }

    const execute = async (forceRefresh = false) => {
      try {
        const tokenOptions = {
          ...(forceRefresh ? { forceRefresh: true } : {}),
          ...(tokenUserId ? { targetUserId: tokenUserId } : {}),
        }
        const accessToken = await getToken(tokenOptions)
        console.log("[GA4] Access token present?", !!accessToken)
        if (!accessToken) throw new Error("Missing Google access token")
        const res = await supabase.functions.invoke("ga4-proxy", {
          body: { ...payload, organizationId: selectedOrgId },
          headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
        })
        console.log("[GA4] Function response", res)
        if (res.error) {
          const ctx = res.error.context || {}
          let body = ctx.body
          if (typeof body === "string") {
            try {
              body = JSON.parse(body)
            } catch (parseErr) {
              console.warn("[GA4] Failed to parse invoke error body", parseErr)
            }
          }
          const msg =
            body?.error?.message ||
            body?.message ||
            ctx.error ||
            res.error.message ||
            "Failed to communicate with GA4 proxy"
          const err = new Error(msg)
          err.context = { ...ctx, body }
          throw err
        }
        return { data: res.data, error: null }
      } catch (err) {
        console.error("[GA4] Invoke failed", err)
        return { data: null, error: err }
      }
    }

    console.log("[GA4] Invoking function", payload)
    if (!skipLoading) {
      setFunctionLoading({
        message: loadingMessage,
        details: loadingDetails,
        progress,
      })
    }

    let forceRefreshToken = false
    let finalData = null
    let finalError = null

    for (let attempt = 0; attempt < 2; attempt++) {
      const result = await execute(forceRefreshToken)
      if (result.error) {
        if (!forceRefreshToken && shouldRetryForError(result.error)) {
          console.log("[GA4] Retrying GA4 request after refreshing access token")
          forceRefreshToken = true
          continue
        }
        finalError = result.error
        break
      }

      if (!forceRefreshToken && shouldRetryForApiError(result.data?.error)) {
        console.log(
          "[GA4] Retrying GA4 request after API reported expired access token"
        )
        forceRefreshToken = true
        continue
      }

      finalData = result.data
      break
    }

    if (!skipLoading) setFunctionLoading(null)

    if (finalError) {
      return { data: null, error: finalError }
    }

    const isRecord = (value) =>
      typeof value === "object" && value !== null && !Array.isArray(value)

    if (isRecord(finalData) && isRecord(finalData.error)) {
      const apiError = finalData.error
      const message =
        typeof apiError.message === "string" && apiError.message.trim()
          ? apiError.message.trim()
          : "Failed to complete GA4 request. Please try again."
      const err = new Error(message)
      const numericCode =
        typeof apiError.code === "number"
          ? apiError.code
          : parseInt(apiError.code ?? "", 10) || null
      const context = { apiError }
      if (Number.isFinite(numericCode)) {
        context.status = numericCode
      } else if (typeof apiError.status === "number") {
        context.status = apiError.status
      }
      if (typeof apiError.status === "string") {
        context.statusText = apiError.status
      }
      err.context = context
      return { data: null, error: err }
    }

    return { data: finalData, error: null }
  }

  const updateFunctionLoading = (overrides = {}) => {
    setFunctionLoading((prev) => {
      const base =
        prev ||
        {
          message: "Processing request…",
          details: "",
          progress: null,
        }
      const nextProgress =
        overrides.progress !== undefined
          ? overrides.progress
          : base.progress ?? null
      return {
        ...base,
        ...overrides,
        progress: nextProgress,
      }
    })
  }

  const handleInvokeResult = async (
    result,
    postAuthAction,
    fallbackMessage = "Failed to complete GA4 request. Please try again."
  ) => {
    const requestAdditionalAuth = async () => {
      console.log("[GA4] Access token missing or expired, refreshing OAuth flow")
      await refreshGoogleAuth(undefined, postAuthAction)
      setError(AUTH_ERROR_MSG)
      return { ok: false, needsAuth: true }
    }

    if (result.error) {
      const status = result.error.context?.status
      const message = result.error.message || fallbackMessage
      if (
        message.includes("Missing Google access token") ||
        status === 401
      ) {
        return requestAdditionalAuth()
      }
      setError(message)
      return { ok: false }
    }

    const apiError = result.data?.error
    if (apiError) {
      const code =
        typeof apiError.code === "number"
          ? apiError.code
          : parseInt(apiError.code ?? "", 10) || null
      const statusText =
        typeof apiError.status === "string"
          ? apiError.status.toUpperCase()
          : ""

      if (code === 401 || statusText === "UNAUTHENTICATED") {
        return requestAdditionalAuth()
      }

      if (code === 403 || statusText === "PERMISSION_DENIED") {
        const message = apiError.message || "Permission denied for GA4 API"
        console.log("[GA4] Permissions error", message)
        setError(message)
        return { ok: false }
      }

      const message =
        apiError.message ||
        fallbackMessage ||
        "Failed to complete GA4 request. Please try again."
      console.warn("[GA4] Unexpected GA4 API error", apiError)
      setError(message)
      return { ok: false }
    }

    return { ok: true, data: result.data }
  }

  const [newIds, setNewIds] = useState([])

  const filteredProperties = useMemo(() => {
    if (!tableSearchTerm.trim()) return savedProperties
    const term = tableSearchTerm.trim().toLowerCase()
    return savedProperties.filter((property) => {
      const id = (property.property_id || "").toLowerCase()
      const name = (property.property_name || "").toLowerCase()
      return id.includes(term) || name.includes(term)
    })
  }, [savedProperties, tableSearchTerm])

  const { auditColumnDefs, auditDataMap } = useMemo(() => {
    const columnDefs = ADVANCED_AUDIT_FIELD_KEYS.map((key) => ({
      key,
      label: formatColumnLabel(key),
    }))

    const dataMap = {}
    for (const [propertyId, entry] of Object.entries(advancedAuditRaw || {})) {
      const settings = entry?.settings || {}
      const fieldValues = {}
      for (const key of ADVANCED_AUDIT_FIELD_KEYS) {
        fieldValues[key] = extractAuditFieldValue(settings, key)
      }
      dataMap[propertyId] = { values: fieldValues, settings }
    }

    return { auditColumnDefs: columnDefs, auditDataMap: dataMap }
  }, [advancedAuditRaw])

  const performanceDataMap = useMemo(() => {
    const result = {}
    for (const prop of savedProperties) {
      const propertyId = prop?.property_id
      if (!propertyId) continue
      result[propertyId] = advancedPerformanceRaw?.[propertyId] || null
    }
    return result
  }, [advancedPerformanceRaw, savedProperties])

  const templateMap = useMemo(() => {
    const map = new Map()
    for (const template of templates || []) {
      if (template?.id != null) map.set(template.id, template)
    }
    return map
  }, [templates])

  const selectedPropertyForEdit = useMemo(() => {
    if (selectedProps.length !== 1) return null
    const targetId = selectedProps[0]
    return savedProperties.find((prop) => prop.property_id === targetId) || null
  }, [selectedProps, savedProperties])
  const handleAdvancedEditClick = useCallback(async () => {
    if (!selectedPropertyForEdit) {
      setEditSelectionError(
        "Please select a single property to edit GA4 settings."
      )
      return
    }
    setEditSelectionError(null)
    openAdvancedEditModal(selectedPropertyForEdit)
    await ensureEditToken()
  }, [
    ensureEditToken,
    openAdvancedEditModal,
    selectedPropertyForEdit,
    setEditSelectionError,
  ])
  const editModalTemplateSettings = useMemo(() => {
    if (!editModalProperty) return null
    const templateRecord =
      editModalProperty.template_id != null
        ? templateMap.get(editModalProperty.template_id)
        : null
    const templateData = templateRecord?.template || null
    if (!templateData?.propertyTypes) return null
    const propertyType =
      editModalSettings?.propertyType ||
      advancedAuditRaw?.[editModalProperty.property_id]?.settings?.propertyType ||
      null
    const propertyTypeMap = templateData.propertyTypes || {}
    return (
      propertyTypeMap?.[propertyType] ||
      propertyTypeMap?.PROPERTY_TYPE_ORDINARY ||
      Object.values(propertyTypeMap).find(
        (value) => value && typeof value === "object"
      ) ||
      null
    )
  }, [
    advancedAuditRaw,
    editModalProperty,
    editModalSettings?.propertyType,
    templateMap,
  ])

  const hasValidEditToken = isGa4EditTokenValid(editTokenRecord)

  const editModalTemplateName =
    editModalProperty?.template_id != null
      ? templateMap.get(editModalProperty.template_id)?.name || null
      : null

  const activeAdvancedColumns = advancedMode
    ? advancedDataset === "audits"
      ? auditColumnDefs
      : PERFORMANCE_COLUMNS
    : []

  const allFilteredSelected =
    filteredProperties.length > 0 &&
    filteredProperties.every((p) => selectedProps.includes(p.property_id))

  const someFilteredSelected = filteredProperties.some((p) =>
    selectedProps.includes(p.property_id)
  )

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate =
        !allFilteredSelected && someFilteredSelected
    }
  }, [allFilteredSelected, someFilteredSelected])

  const handleToggleSelectAll = (event) => {
    const { checked } = event.target
    setSelectedProps((prev) => {
      const filteredIds = filteredProperties.map((p) => p.property_id)
      if (checked) {
        const next = new Set(prev)
        filteredIds.forEach((id) => next.add(id))
        return Array.from(next)
      }
      const filteredIdSet = new Set(filteredIds)
      return prev.filter((id) => !filteredIdSet.has(id))
    })
  }

  useEffect(() => {
    if (!editSelectionError) return
    if (selectedProps.length === 1) {
      setEditSelectionError(null)
    }
  }, [editSelectionError, selectedProps])

  useEffect(() => {
    if (!advancedMode || advancedDataset !== "audits") {
      setEditSelectionError(null)
    }
  }, [advancedDataset, advancedMode])

  const checkNewAccounts = async () => {
    console.log("[GA4] Checking for new accounts")
    setError(null)
    const existingProperties = new Set(
      savedProperties.map((p) => p.property_id)
    )

    const showPropertySelectionModal = (properties, totals = null) => {
      const sourceList = Array.isArray(properties) ? properties : []
      const filtered = sourceList.filter((p) => {
        const propertyId = typeof p?.property === "string" ? p.property : null
        return propertyId && !existingProperties.has(propertyId)
      })
      const sortedFiltered = sortAvailableProperties(filtered)
      setAvailableProps(sortedFiltered)
      setSelectedNewProps([])
      setModalAccountFilter("")
      setModalPropertyFilter("")
      const fallbackTotals = totals || {
        accountsProcessed: 0,
        totalAccounts: 0,
        propertiesFetched: sourceList.length,
        totalProperties: sourceList.length,
      }
      setLastListingTotals(fallbackTotals)
      setShowSelectModal(true)
    }

    const cachedListing = propertyListingCacheRef.current
    if (cachedListing && Array.isArray(cachedListing.properties)) {
      console.log("[GA4] Using cached GA4 property listing")
      showPropertySelectionModal(
        cachedListing.properties,
        cachedListing.totals || null,
      )
      return
    }

    setLastListingTotals(null)
    const hasAccessToken = await ensureAccessToken("checkNewAccounts")
    if (!hasAccessToken) return
    setListingProperties(true)
    setListingProgress({
      accountsProcessed: 0,
      totalAccounts: 0,
      propertiesFetched: 0,
      totalProperties: 0,
      currentAccount: null,
    })
    listingCancelRef.current = false

    const stopListing = () => {
      setListingProperties(false)
      setListingProgress(null)
    }

    const bailIfCancelled = () => {
      if (listingCancelRef.current) {
        console.log("[GA4] Listing cancelled by user")
        return true
      }
      return false
    }

    try {
      const accounts = []
      let nextPageToken = null
      do {
        if (bailIfCancelled()) return
        const payload = { type: "list-accounts" }
        if (nextPageToken) payload.pageToken = nextPageToken
        const result = await invoke(payload, { skipLoading: true })
        const handled = await handleInvokeResult(
          result,
          "checkNewAccounts",
          "Failed to list GA4 accounts. Please try again."
        )
        if (!handled.ok) {
          stopListing()
          return
        }
        if (bailIfCancelled()) return
        const response = handled.data || {}
        const batch = Array.isArray(response.accounts)
          ? response.accounts
          : []
        accounts.push(...batch)
        nextPageToken = response.nextPageToken || null
      } while (nextPageToken)

      if (bailIfCancelled()) {
        stopListing()
        return
      }

      const totalAccounts = accounts.length
      const totalPropertiesExpected = accounts.reduce(
        (sum, acc) => sum + (acc.propertyCount || 0),
        0
      )
      setListingProgress((prev) =>
        prev
          ? {
              ...prev,
              totalAccounts,
              totalProperties: totalPropertiesExpected,
            }
          : prev
      )

      const allProperties = []
      for (const acc of accounts) {
        if (bailIfCancelled()) {
          stopListing()
          return
        }
        if (!acc || !acc.account) {
          setListingProgress((prev) =>
            prev
              ? {
                  ...prev,
                  accountsProcessed: prev.accountsProcessed + 1,
                  currentAccount: null,
                }
              : prev
          )
          continue
        }
        const accountLabel = acc.displayName
          ? `${acc.displayName} (${acc.account})`
          : acc.account
        setListingProgress((prev) =>
          prev
            ? {
                ...prev,
                currentAccount: accountLabel || null,
              }
            : prev
        )

        let propertyToken = null
        do {
          if (bailIfCancelled()) {
            stopListing()
            return
          }
          const payload = { type: "list-account-properties", account: acc.account }
          if (propertyToken) payload.pageToken = propertyToken
          const result = await invoke(payload, { skipLoading: true })
          const handled = await handleInvokeResult(
            result,
            "checkNewAccounts",
            `Failed to list GA4 properties for account ${acc.account}. Please try again.`
          )
          if (!handled.ok) {
            stopListing()
            return
          }
          if (bailIfCancelled()) {
            stopListing()
            return
          }
          const response = handled.data || {}
          const batch = Array.isArray(response.properties)
            ? response.properties
            : []
          const accountId = response.account || acc.account
          if (batch.length) {
            allProperties.push(
              ...batch.map((p) => ({
                ...p,
                account: accountId,
                accountDisplayName: acc.displayName || "",
                accountLabel,
              }))
            )
          }
          setListingProgress((prev) => {
            if (!prev) return prev
            const propertiesFetched = prev.propertiesFetched + batch.length
            const totalProperties =
              typeof prev.totalProperties === "number"
                ? Math.max(prev.totalProperties, propertiesFetched)
                : prev.totalProperties
            return {
              ...prev,
              propertiesFetched,
              totalProperties,
            }
          })
          propertyToken = response.nextPageToken || null
        } while (propertyToken)

        setListingProgress((prev) =>
          prev
            ? {
                ...prev,
                accountsProcessed: prev.accountsProcessed + 1,
                currentAccount: null,
              }
            : prev
        )
      }

      const parsedProperties = allProperties.map((p) => ({
        ...p,
        property: p.property,
      }))
      const propertyMap = new Map()
      parsedProperties.forEach((prop) => {
        if (!prop.property) return
        if (!propertyMap.has(prop.property)) propertyMap.set(prop.property, prop)
      })
      const uniqueProperties = Array.from(propertyMap.values())
      console.log("[GA4] Properties parsed", uniqueProperties)

      const totals = {
        accountsProcessed: totalAccounts,
        totalAccounts,
        propertiesFetched: uniqueProperties.length,
        totalProperties: Math.max(
          totalPropertiesExpected,
          uniqueProperties.length
        ),
      }
      if (bailIfCancelled()) {
        stopListing()
        return
      }
      propertyListingCacheRef.current = {
        properties: uniqueProperties,
        totals,
      }
      setLastListingTotals(totals)
      stopListing()
      showPropertySelectionModal(uniqueProperties, totals)
    } catch (err) {
      console.error("[GA4] Failed to list GA4 properties", err)
      stopListing()
      setError(err.message || "Failed to list GA4 properties. Please try again.")
    }
  }

  const cancelListing = () => {
    listingCancelRef.current = true
    setListingProperties(false)
    setListingProgress(null)
  }

  const addSelectedProperties = async () => {
    const toAdd = availableProps.filter((p) =>
      selectedNewProps.includes(p.property)
    )
    if (toAdd.length === 0) {
      setShowSelectModal(false)
      return
    }
    const getPropertyTypeForAvailableProp = (prop) => {
      if (!prop || typeof prop !== "object") return DEFAULT_PROPERTY_TYPE
      if (typeof prop.propertyType === "string") {
        return normalizePropertyTypeKey(prop.propertyType)
      }
      if (typeof prop?.property?.propertyType === "string") {
        return normalizePropertyTypeKey(prop.property.propertyType)
      }
      return DEFAULT_PROPERTY_TYPE
    }
    const inserts = toAdd.map((p) => {
      const propertyTypeKey = getPropertyTypeForAvailableProp(p)
      const templateId = defaultTemplateMap[propertyTypeKey]
      if (selectedOrgId) {
        const record = {
          property_id: p.property,
          property_name: p.displayName,
          organization_id: selectedOrgId,
          ...(user?.id ? { user_id: user.id } : {}),
          ...(templateId ? { template_id: templateId } : {}),
        }
        if (normalizedUserEmail) {
          record.google_account_email = normalizedUserEmail
        }
        if (selectedAccountId) {
          record.account_id = selectedAccountId
        }
        return record
      }
      return {
        property_id: p.property,
        property_name: p.displayName,
        user_id: user.id,
        ...(templateId ? { template_id: templateId } : {}),
        ...(normalizedUserEmail
          ? { google_account_email: normalizedUserEmail }
          : {}),
      }
    })
    const { data: inserted } = await supabase
      .from("ga4_properties")
      .insert(inserts)
      .select(
        "id, property_id, property_name, template_id, google_account_email, organization_id, account_id, user_id",
      )
    const newIds = (inserted || [])
      .map((r) => r?.property_id)
      .filter((id) => typeof id === "string")
    const toAddById = new Map(toAdd.map((p) => [p.property, p]))
    const propertyRecordsMap = new Map()
    for (const record of inserted || []) {
      const propertyId = typeof record?.property_id === "string" ? record.property_id : null
      if (!propertyId) continue
      const source = toAddById.get(propertyId) || {}
      propertyRecordsMap.set(propertyId, {
        ...source,
        ...record,
        property_id: propertyId,
        property_name:
          record?.property_name ||
          source?.displayName ||
          (typeof source?.property?.displayName === "string"
            ? source.property.displayName
            : propertyId),
      })
    }

    await loadSavedProperties(newIds)

    if (propertyListingCacheRef.current?.properties) {
      const addedSet = new Set(newIds)
      propertyListingCacheRef.current = {
        ...propertyListingCacheRef.current,
        properties: propertyListingCacheRef.current.properties.filter(
          (prop) => !addedSet.has(prop?.property),
        ),
      }
    }

    setAvailableProps((prev) =>
      prev.filter((p) => !newIds.includes(p.property))
    )
    setSelectedNewProps([])
    setModalAccountFilter("")
    setModalPropertyFilter("")
    setShowSelectModal(false)

    if (newIds.length) {
      setSelectedProps((prev) => {
        const next = new Set(prev)
        newIds.forEach((id) => next.add(id))
        return Array.from(next)
      })
      await runSettingsAudit(newIds, { propertyRecords: propertyRecordsMap })
    }
  }

  const loadSavedProperties = useCallback(async (highlight = []) => {
    console.log("[GA4] Loading saved properties")
    if (!user && !selectedOrgId) return
    const columnCandidates = [
      "id, property_id, property_name, template_id, google_account_email, organization_id, account_id, user_id, does_not_use_google_ads",
      "id, property_id, property_name, template_id, google_account_email, organization_id, user_id, does_not_use_google_ads",
      "id, property_id, property_name, template_id, google_account_email, user_id, does_not_use_google_ads",
      "id, property_id, property_name, google_account_email, organization_id, account_id, user_id, does_not_use_google_ads",
      "id, property_id, property_name, google_account_email, organization_id, user_id, does_not_use_google_ads",
      "id, property_id, property_name, google_account_email, user_id, does_not_use_google_ads",
    ]
    let data = null
    let error = null
    for (const columns of columnCandidates) {
      let query = supabase.from("ga4_properties").select(columns)
      if (selectedOrgId) query = query.eq("organization_id", selectedOrgId)
      else if (user) query = query.eq("user_id", user.id)
      ;({ data, error } = await query)
      if (!error) break
    }
    console.log("[GA4] Saved properties result", { data, error })
    if (error || !data) return
    const withAudit = await Promise.all(
      data.map(async (row) => {
        const { data: setting } = await supabase
          .from("ga4_property_settings")
          .select("created_at, issue_count")
          .eq("ga4_property_id", row.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
        return {
          ...row,
          last_audit: setting?.created_at || null,
          issue_count:
            typeof setting?.issue_count === "number" ? setting.issue_count : 0,
        }
      })
    )
    const sortByFirstColumn = (rows) =>
      rows.sort((a, b) => {
        const nameCompare = (a.property_name || "").localeCompare(
          b.property_name || "",
          undefined,
          { sensitivity: "base" }
        )
        if (nameCompare !== 0) return nameCompare
        return (a.property_id || "").localeCompare(
          b.property_id || "",
          undefined,
          { sensitivity: "base" }
        )
      })

    const sorted = [...withAudit]
    sortByFirstColumn(sorted)

    setSavedProperties(sorted)
    if (highlight.length) {
      const highlightSet = new Set(highlight)
      const visibleHighlights = sorted
        .map((row) => row.property_id)
        .filter((id) => highlightSet.has(id))
      setNewIds(visibleHighlights)
    } else {
      setNewIds([])
    }
  }, [selectedOrgId, user])

  useEffect(() => {
    if (!user && !selectedOrgId) return
    let isActive = true
    const init = async () => {
      await loadSavedProperties()
      try {
        const list = await listTemplates(selectedOrgId)
        if (isActive) setTemplates(list)
      } catch (e) {
        console.error("[GA4] Failed to load templates", e)
      }
      const action = localStorage.getItem(POST_AUTH_ACTION_KEY)
      if (action) {
        localStorage.removeItem(POST_AUTH_ACTION_KEY)
        console.log("[GA4] Cleared pending post-auth action", action)
      }
    }
    init()
    return () => {
      isActive = false
    }
  }, [user, selectedOrgId, loadSavedProperties])

  useEffect(() => {
    propertyListingCacheRef.current = null
  }, [selectedOrgId, user?.id])

  useEffect(() => {
    const localMap = ensureDefaultTemplateMap(
      loadDefaultTemplateMap(defaultTemplateContextKey),
    )
    setDefaultTemplateMap(localMap)
    let isActive = true
    const syncDefaults = async () => {
      try {
        const map = await refreshDefaultTemplateMap(defaultTemplateContextKey)
        if (isActive && map) {
          setDefaultTemplateMap(ensureDefaultTemplateMap(map))
        }
      } catch (err) {
        console.warn("[GA4] Failed to refresh default templates", err)
      }
    }
    syncDefaults()
    const unsubscribe = subscribeToDefaultTemplateChanges((detail) => {
      if (!detail || detail.contextKey !== defaultTemplateContextKey) return
      if (!isActive) return
      setDefaultTemplateMap(ensureDefaultTemplateMap(detail.map))
    })
    return () => {
      isActive = false
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [defaultTemplateContextKey])

  useEffect(() => {
    const userId = user?.id
    if (!userId) {
      silentRefreshAttemptRef.current = null
      return
    }
    if (silentRefreshAttemptRef.current === userId) return
    silentRefreshAttemptRef.current = userId
    let isActive = true
    const refreshSilently = async () => {
      try {
        const tokens = await loadTokens(supabase, userId)
        if (!isActive) return
        if (tokens?.refresh_token) {
          const refreshed = await getValidAccessToken(supabase, user)
          if (!isActive) return
          if (refreshed) {
            console.log("[GA4] Silently refreshed GA4 access token")
          }
        }
      } catch (err) {
        if (isActive)
          console.warn("[GA4] Silent GA4 token refresh failed", err)
      }
    }
    refreshSilently()
    return () => {
      isActive = false
    }
  }, [user])

  useEffect(() => {
    if (!advancedMode) {
      setAdvancedLoading(false)
      return
    }

    const propertyPairs = savedProperties
      .map((prop) => [prop.id, prop.property_id])
      .filter(([internalId, propId]) => internalId && propId)

    if (propertyPairs.length === 0) {
      if (advancedDataset === "audits") setAdvancedAuditRaw({})
      else setAdvancedPerformanceRaw({})
      setAdvancedLoading(false)
      return
    }

    const idToPropertyId = new Map(propertyPairs)
    let cancelled = false

    const fetchAdvancedData = async () => {
      setAdvancedLoading(true)
      try {
        if (advancedDataset === "audits") {
          const { data, error } = await supabase
            .from("ga4_property_settings")
            .select("ga4_property_id, settings, created_at, issue_count")
            .in("ga4_property_id", Array.from(idToPropertyId.keys()))
            .order("created_at", { ascending: false })
          if (error) throw error
          const latest = {}
          for (const row of data || []) {
            const propertyId = idToPropertyId.get(row.ga4_property_id)
            if (!propertyId) continue
            if (latest[propertyId]) continue
            latest[propertyId] = {
              settings: row.settings || {},
              created_at: row.created_at || null,
              issue_count:
                typeof row.issue_count === "number" ? row.issue_count : 0,
            }
          }
          if (!cancelled) setAdvancedAuditRaw(latest)
        } else {
          const propertyIdList = Array.from(idToPropertyId.keys())
          const [
            { data: eventRows, error: eventError },
            { data: ccrRows, error: ccrError },
          ] = await Promise.all([
            supabase
              .from("ga4_event_audit_results")
              .select(
                "ga4_property_id, event_owner, event_count, event_details, monthly_breakdown"
              )
              .in("ga4_property_id", propertyIdList),
            supabase
              .from("ga4_ccr_results")
              .select(
                "ga4_property_id, event_owner, product_name, event_count, monthly_breakdown"
              )
              .in("ga4_property_id", propertyIdList),
          ])
          if (eventError) throw eventError
          if (ccrError) throw ccrError

          const metricsMap = new Map()
          const ensureMetrics = (propertyId) => {
            if (!metricsMap.has(propertyId)) {
              metricsMap.set(propertyId, {
                eventAuditTotal: 0,
                fcrEventCount: 0,
                fcrOwnerSet: new Set(),
                fcrMonths: new Set(),
                ccrEventCount: 0,
                ccrOwnerSet: new Set(),
                ccrProductSet: new Set(),
                ccrMonths: new Set(),
              })
            }
            return metricsMap.get(propertyId)
          }

          for (const row of eventRows || []) {
            const propertyId = idToPropertyId.get(row.ga4_property_id)
            if (!propertyId) continue
            const metrics = ensureMetrics(propertyId)
            metrics.eventAuditTotal += normalizeAdvancedNumeric(row?.event_count)
            let ownerHasFcrEvent = false
            const details = Array.isArray(row?.event_details)
              ? row.event_details
              : []
            for (const detail of details) {
              const eventName =
                typeof detail?.eventName === "string"
                  ? detail.eventName.toLowerCase()
                  : ""
              const count = normalizeAdvancedNumeric(detail?.eventCount)
              if (FCR_EVENT_NAMES.has(eventName)) {
                metrics.fcrEventCount += count
                if (count > 0) ownerHasFcrEvent = true
              }
            }
            if (ownerHasFcrEvent) {
              const ownerLabel =
                typeof row?.event_owner === "string" && row.event_owner.trim()
                  ? row.event_owner.trim()
                  : "(unknown)"
              metrics.fcrOwnerSet.add(ownerLabel)
            }
            const monthly = Array.isArray(row?.monthly_breakdown)
              ? row.monthly_breakdown
              : []
            for (const month of monthly) {
              const monthKey =
                typeof month?.yearMonth === "string" ? month.yearMonth : null
              if (monthKey) metrics.fcrMonths.add(monthKey)
            }
          }

          for (const row of ccrRows || []) {
            const propertyId = idToPropertyId.get(row.ga4_property_id)
            if (!propertyId) continue
            const metrics = ensureMetrics(propertyId)
            metrics.ccrEventCount += normalizeAdvancedNumeric(row?.event_count)
            const ownerLabel =
              typeof row?.event_owner === "string" && row.event_owner.trim()
                ? row.event_owner.trim()
                : "(unknown)"
            metrics.ccrOwnerSet.add(ownerLabel)
            const productLabel =
              typeof row?.product_name === "string" && row.product_name.trim()
                ? row.product_name.trim()
                : "(unspecified)"
            metrics.ccrProductSet.add(productLabel)
            const monthly = Array.isArray(row?.monthly_breakdown)
              ? row.monthly_breakdown
              : []
            for (const month of monthly) {
              const monthKey =
                typeof month?.yearMonth === "string" ? month.yearMonth : null
              if (monthKey) metrics.ccrMonths.add(monthKey)
            }
          }

          const allPropertyIds = new Set(idToPropertyId.values())
          const finalMetrics = {}
          for (const propertyId of allPropertyIds) {
            const metrics = metricsMap.get(propertyId)
            if (metrics) {
              finalMetrics[propertyId] = {
                eventAuditTotal: metrics.eventAuditTotal,
                fcrEventCount: metrics.fcrEventCount,
                fcrEventOwners: metrics.fcrOwnerSet.size,
                fcrMonthsTracked: metrics.fcrMonths.size,
                ccrEventCount: metrics.ccrEventCount,
                ccrEventOwners: metrics.ccrOwnerSet.size,
                ccrProducts: metrics.ccrProductSet.size,
                ccrMonthsTracked: metrics.ccrMonths.size,
              }
            } else {
              finalMetrics[propertyId] = {
                eventAuditTotal: 0,
                fcrEventCount: 0,
                fcrEventOwners: 0,
                fcrMonthsTracked: 0,
                ccrEventCount: 0,
                ccrEventOwners: 0,
                ccrProducts: 0,
                ccrMonthsTracked: 0,
              }
            }
          }
          if (!cancelled) setAdvancedPerformanceRaw(finalMetrics)
        }
      } catch (err) {
        console.error("[GA4] Failed to load advanced GA4 data", err)
        if (!cancelled) {
          setError(
            err?.message || "Failed to load advanced GA4 data. Please try again."
          )
        }
      } finally {
        if (!cancelled) setAdvancedLoading(false)
      }
    }

    fetchAdvancedData()

    return () => {
      cancelled = true
    }
  }, [
    advancedMode,
    advancedDataset,
    advancedReloadNonce,
    savedProperties,
  ])

  const updateTemplate = async (propertyId, templateId) => {
    const property = savedProperties.find((p) => p.property_id === propertyId)
    if (!property) return
    const { error } = await supabase
      .from("ga4_properties")
      .update({ template_id: templateId || null })
      .eq("id", property.id)
    if (!error) {
      setSavedProperties((prev) =>
        prev.map((p) =>
          p.property_id === propertyId
            ? { ...p, template_id: templateId || null }
            : p
        )
      )
    } else {
      console.error("[GA4] Failed to update template", error)
    }
  }

  const openAssignTemplateModal = () => {
    if (selectedProps.length === 0) return
    const selectedRecords = savedProperties.filter((p) =>
      selectedProps.includes(p.property_id)
    )
    const uniqueTemplateIds = new Set(
      selectedRecords.map((p) => p.template_id || "")
    )
    if (uniqueTemplateIds.size === 1) {
      const [value] = uniqueTemplateIds
      setAssignTemplateId(value || "")
    } else {
      setAssignTemplateId("")
    }
    setAssignTemplateError(null)
    setAssignTemplateModalOpen(true)
  }

  const closeAssignTemplateModal = useCallback(() => {
    if (assignTemplateLoading) return
    setAssignTemplateModalOpen(false)
    setAssignTemplateError(null)
    setAssignTemplateId("")
  }, [assignTemplateLoading])

  const assignTemplateToSelected = async () => {
    if (selectedProps.length === 0) return
    setAssignTemplateLoading(true)
    setAssignTemplateError(null)
    try {
      const templateId = assignTemplateId ? assignTemplateId : null
      const ids = selectedProps
        .map((pid) => savedProperties.find((p) => p.property_id === pid)?.id)
        .filter(Boolean)
      if (!ids.length) {
        setAssignTemplateError(
          "Unable to determine the selected GA4 properties."
        )
        setAssignTemplateLoading(false)
        return
      }
      const { error } = await supabase
        .from("ga4_properties")
        .update({ template_id: templateId })
        .in("id", ids)
      if (error) throw error
      const idSet = new Set(ids)
      setSavedProperties((prev) =>
        prev.map((p) =>
          idSet.has(p.id) ? { ...p, template_id: templateId } : p
        )
      )
      setAssignTemplateModalOpen(false)
      setAssignTemplateId("")
      setAssignTemplateError(null)
    } catch (err) {
      console.error("[GA4] Failed to assign template to selected properties", err)
      setAssignTemplateError(
        err?.message || "Failed to assign template. Please try again."
      )
    } finally {
      setAssignTemplateLoading(false)
    }
  }

  useEffect(() => {
    if (assignTemplateModalOpen && selectedProps.length === 0 && !assignTemplateLoading) {
      closeAssignTemplateModal()
    }
  }, [
    assignTemplateModalOpen,
    selectedProps.length,
    assignTemplateLoading,
    closeAssignTemplateModal,
  ])

  const deleteSelectedProperties = async () => {
    if (!window.confirm("Remove selected GA4 properties?")) return

    const records = selectedProps
      .map((pid) => savedProperties.find((p) => p.property_id === pid))
      .filter(Boolean)

    if (records.length === 0) return

    const ids = records.map((record) => record.id).filter(Boolean)
    const accountIds = new Set(
      records
        .map((record) =>
          typeof record?.account_id === "string" ? record.account_id : null,
        )
        .filter(Boolean),
    )

    if (ids.length > 0) {
      const { error } = await supabase
        .from("ga4_properties")
        .delete()
        .in("id", ids)
      if (error) {
        console.error("[GA4] Failed to delete GA4 properties", error)
        return
      }
    }

    setSavedProperties((prev) =>
      prev.filter((p) => !selectedProps.includes(p.property_id))
    )
    setSelectedProps([])

    if (accountIds.size > 0) {
      const deletedAccountIds = await deleteAccountsIfEmpty(accountIds, supabase)
      if (deletedAccountIds.length > 0 && selectedOrgId) {
        await reconcileDealershipBilling(selectedOrgId)
      }
    }
  }

  const runSettingsAudit = async (propertyIdsParam, options = {}) => {
    const resolvedIds = Array.isArray(propertyIdsParam)
      ? propertyIdsParam.filter((pid) => typeof pid === "string" && pid)
      : selectedProps
    const targetPropertyIds = Array.from(new Set(resolvedIds))
    console.log(
      "[GA4] Running settings, key event, and event audit",
      targetPropertyIds,
    )
    setError(null)
    if (targetPropertyIds.length === 0) return

    const overrideRecordsRaw = options?.propertyRecords
    let overrideRecords = null
    if (overrideRecordsRaw) {
      if (overrideRecordsRaw instanceof Map) {
        overrideRecords = overrideRecordsRaw
      } else if (Array.isArray(overrideRecordsRaw)) {
        overrideRecords = new Map(overrideRecordsRaw)
      } else if (typeof overrideRecordsRaw === "object") {
        overrideRecords = new Map(Object.entries(overrideRecordsRaw))
      }
    }

    const totalProperties = targetPropertyIds.length
    const totalSteps = auditSteps.length || 1
    const formatDetails = (propertyIndex, propertyName) => {
      if (totalProperties === 1) {
        return propertyName
          ? `Auditing ${propertyName}`
          : "Auditing GA4 property"
      }
      const suffix = propertyName ? ` (${propertyName})` : ""
      return `Auditing property ${propertyIndex} of ${totalProperties}${suffix}`
    }

    const setProgress = (propertyIndex, stepIndex, stepLabel, propertyName) => {
      updateFunctionLoading({
        message: "Running GA4 audit…",
        details: formatDetails(propertyIndex, propertyName),
        progress: {
          currentItem: propertyIndex,
          totalItems: totalProperties,
          currentStep: stepIndex,
          totalSteps,
          stepLabel: stepLabel || "",
        },
      })
    }
    const setAuditStepProgress = (propertyIndex, stepKey, propertyName) => {
      const stepIndex = auditSteps.findIndex((step) => step.key === stepKey)
      if (stepIndex < 0) return
      setProgress(
        propertyIndex,
        stepIndex + 1,
        auditSteps[stepIndex]?.label,
        propertyName,
      )
    }

    try {
      updateFunctionLoading({
        message: "Running GA4 audit…",
        details:
          totalProperties === 1
            ? "Auditing selected GA4 property…"
            : `Preparing to audit ${totalProperties} properties…`,
        progress: {
          currentItem: 0,
          totalItems: totalProperties,
          currentStep: 0,
          totalSteps,
          stepLabel: "",
        },
      })

      for (let index = 0; index < totalProperties; index++) {
        const pid = targetPropertyIds[index]
        const propertyNumber = index + 1
        let prop = overrideRecords?.get(pid)
        if (!prop) {
          prop = savedProperties.find((p) => p.property_id === pid)
        }
        if (!prop) {
          let fallbackQuery = supabase
            .from("ga4_properties")
            .select(
              "id, property_id, property_name, template_id, google_account_email, organization_id, account_id, user_id",
            )
            .eq("property_id", pid)
          if (selectedOrgId)
            fallbackQuery = fallbackQuery.eq("organization_id", selectedOrgId)
          else if (user?.id)
            fallbackQuery = fallbackQuery.eq("user_id", user.id)
          const { data: fallback } = await fallbackQuery.maybeSingle()
          if (fallback) {
            prop = fallback
            if (overrideRecords) {
              overrideRecords.set(pid, fallback)
            }
          }
        }
        if (!prop) {
          console.warn(
            "[GA4] Unable to resolve stored GA4 property record for audit",
            pid,
          )
          continue
        }
        const displayName = prop?.property_name || pid
        const tokenUserId = getPropertyTokenUserId(prop)
        const templateRecord =
          prop?.template_id != null
            ? templates.find((t) => t.id === prop.template_id) || null
            : null
        let eventAuditRowsForSync = null

        setAuditStepProgress(propertyNumber, "settings", displayName)
        let extendedAuditData = null
        for (const sections of EXTENDED_AUDIT_CHUNKS) {
          const {
            data: chunkData,
            error: chunkError,
          } = await invoke(
            {
              type: "extended-audit",
              propertyId: normalizePropertyId(pid),
              sections,
            },
            { skipLoading: true, tokenUserId }
          )
          console.log("[GA4] Extended audit chunk result", pid, sections, {
            data: chunkData,
            error: chunkError,
          })
          if (chunkError) {
            const status = chunkError.context?.status
            if (
              chunkError.message.includes("Missing Google access token") ||
              status === 401
            ) {
              setError(AUTH_ERROR_MSG)
              return
            } else {
              setError(chunkError.message)
              return
            }
          }
          if (chunkData) {
            extendedAuditData = mergeExtendedAuditData(
              extendedAuditData,
              chunkData
            )
          }
        }

        const data = extendedAuditData
        if (!data) continue

        const measurementIds = extractMeasurementIdsFromSettingsData(data)
        const normalizedMeasurementIds = Array.from(
          new Set(measurementIds.map((id) => normalizeMeasurementId(id)).filter(Boolean)),
        )
        let measurementScripts = {}
        if (measurementIds.length) {
          measurementScripts = await fetchGa4MeasurementScripts(measurementIds)
          if (measurementIds.length) {
            data.ga4MeasurementIds = measurementIds
          }
          const scriptEntries = Object.entries(measurementScripts).filter(
            ([, script]) => typeof script === "string" && script,
          )
          if (scriptEntries.length > 0) {
            data.ga4MeasurementScripts = Object.fromEntries(scriptEntries)
          }
        }

        let latestSettingsRowId = null
        setAuditStepProgress(propertyNumber, "userAccess", displayName)
        const userAccessRows = normalizeUserAccessAuditPayload(data)
        const userAccessStatus =
          typeof data.userAccessAuditStatus === "string" &&
          data.userAccessAuditStatus
            ? data.userAccessAuditStatus
            : "ok"
        let userAccessIssueCount = 0
        let confirmationIdentifiers = []
        if (prop?.id) {
          const { error: accessDeleteError } = await supabase
            .from("ga4_user_access_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (accessDeleteError) {
            console.error(
              "[GA4] Failed to clear previous GA4 user access audit results",
              accessDeleteError,
            )
          }
          if (userAccessRows.length) {
            const insertRows = userAccessRows.map((row) => ({
              ga4_property_id: prop.id,
              identifier: row.identifier || null,
              normalized_identifier: row.normalizedIdentifier || null,
              principal_email: row.principalEmail || null,
              principal_type: row.principalType || null,
              user_link_name: row.userLinkName || null,
              direct_roles: Array.isArray(row.directRoles)
                ? row.directRoles
                : [],
              effective_roles: Array.isArray(row.effectiveRoles)
                ? row.effectiveRoles
                : [],
              access_level:
                typeof row.accessLevel === "string" && row.accessLevel
                  ? row.accessLevel
                  : null,
              raw_audit: row.raw || null,
            }))
            const { error: accessInsertError } = await supabase
              .from("ga4_user_access_audit_results")
              .insert(insertRows)
            if (accessInsertError) {
              console.error(
                "[GA4] Failed to store GA4 user access audit results",
                accessInsertError,
              )
            }
          }
          const fingerprint = createUserAccessFingerprint(userAccessRows)
          const nowIso = new Date().toISOString()
          const { error: stateError } = await supabase
            .from("ga4_user_access_audit_states")
            .upsert({
              ga4_property_id: prop.id,
              audit_status: userAccessStatus,
              audit_reason:
                typeof data.userAccessAuditReason === "string"
                  ? data.userAccessAuditReason
                  : null,
              audit_message:
                typeof data.userAccessAuditMessage === "string"
                  ? data.userAccessAuditMessage
                  : null,
              user_count: userAccessRows.length,
              user_fingerprint: fingerprint,
              last_audited_at: nowIso,
              updated_at: nowIso,
            })
          if (stateError) {
            console.error(
              "[GA4] Failed to store GA4 user access audit state",
              stateError,
            )
          }
          const { data: confirmationData, error: confirmationError } = await supabase
            .from("ga4_user_access_confirmations")
            .select("confirmed_identifiers")
            .eq("ga4_property_id", prop.id)
            .maybeSingle()
          if (confirmationError) {
            console.error(
              "[GA4] Failed to load GA4 user access confirmation",
              confirmationError,
            )
          } else if (
            confirmationData &&
            Array.isArray(confirmationData.confirmed_identifiers)
          ) {
            confirmationIdentifiers = confirmationData.confirmed_identifiers
          }
        }
        const expectedUsers = extractTemplateExpectedUsers(
          templateRecord?.template,
        )
        const userAccessEvaluation = evaluateUserAccessIssues({
          rows: userAccessRows,
          status: userAccessStatus,
          confirmationIdentifiers,
          expectedUsers,
        })
        userAccessIssueCount = userAccessEvaluation.issueCount

        let totalIssueCount =
          countSettingsAuditIssues(data) + userAccessIssueCount
        if (prop?.id) {
          const { error: settingsDeleteError } = await supabase
            .from("ga4_property_settings")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (settingsDeleteError) {
            console.error(
              "[GA4] Failed to clear previous GA4 settings",
              settingsDeleteError,
            )
          }
          const {
            data: insertedSettings,
            error: settingsInsertError,
          } = await supabase
            .from("ga4_property_settings")
            .insert({
              ga4_property_id: prop.id,
              settings: data,
              issue_count: totalIssueCount,
            })
            .select("id, created_at, issue_count")
            .single()
          if (settingsInsertError) {
            console.error("[GA4] Failed to save GA4 settings", settingsInsertError)
          } else {
            latestSettingsRowId = insertedSettings?.id || null
            if (typeof insertedSettings?.issue_count === "number") {
              totalIssueCount = insertedSettings.issue_count
            }
            setAuditSaved((prev) => ({ ...prev, [pid]: true }))
            await pruneHistoricalAuditRows("ga4_property_settings", prop.id)
          }
        }

        setAuditStepProgress(propertyNumber, "keyEvents", displayName)
        const {
          data: eventsData,
          error: eventsError,
        } = await invoke(
          {
            type: "key-events",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Key events result", pid, {
          data: eventsData,
          error: eventsError,
        })
        if (eventsError) {
          const status = eventsError.context?.status
          if (
            eventsError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(eventsError.message)
            return
          }
        }
        if (prop?.id && eventsData) {
          const { error: keyEventsDeleteError } = await supabase
            .from("ga4_property_key_events")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (keyEventsDeleteError) {
            console.error(
              "[GA4] Failed to clear previous GA4 key events",
              keyEventsDeleteError,
            )
          }
          const { error: keyEventsError } = await supabase
            .from("ga4_property_key_events")
            .insert({ ga4_property_id: prop.id, events: eventsData })
            .select("id, created_at")
            .single()
          if (keyEventsError) {
            console.error("[GA4] Failed to store key events", keyEventsError)
          } else {
            await pruneHistoricalAuditRows("ga4_property_key_events", prop.id)
          }
        }

        if (isAutomotive) {
        setAuditStepProgress(propertyNumber, "eventAudit", displayName)
        const {
          data: eventAuditData,
          error: eventAuditError,
        } = await invoke(
          {
            type: "event-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Event audit result", pid, {
          data: eventAuditData,
          error: eventAuditError,
        })
        if (eventAuditError) {
          const status = eventAuditError.context?.status
          if (
            eventAuditError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(eventAuditError.message)
            return
          }
        }
        if (prop?.id && Array.isArray(eventAuditData)) {
          await supabase
            .from("ga4_event_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (eventAuditData.length) {
            await supabase
              .from("ga4_event_audit_results")
              .insert(
                eventAuditData.map((e) => ({
                  ga4_property_id: prop.id,
                  event_owner: e.eventOwner,
                  first_detected: e.firstDate,
                  last_detected: e.lastDate || null,
                  event_count: e.eventCount,
                  event_details: e.eventDetails || [],
                  monthly_breakdown: e.monthlyBreakdown || [],
                }))
              )
          }
          const hasEventOwners = eventAuditData.some(
            (row) => typeof row?.eventOwner === "string" && row.eventOwner.trim(),
          )
          if (hasEventOwners && prop?.account_id) {
            await applyEventOwnersToAccountProducts({
              accountId: prop.account_id,
              organizationId: prop?.organization_id,
              ownerRows: eventAuditData,
            })
          }
        }

        if (Array.isArray(eventAuditData) && eventAuditData.length > 0) {
          eventAuditRowsForSync = eventAuditData
        } else {
          eventAuditRowsForSync = null
        }
        }

        setAuditStepProgress(propertyNumber, "pageLocationAudit", displayName)
        const {
          data: pageLocationAuditData,
          error: pageLocationAuditError,
        } = await invoke(
          {
            type: "page-location-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Page location audit result", pid, {
          data: pageLocationAuditData,
          error: pageLocationAuditError,
        })
        if (pageLocationAuditError) {
          const status = pageLocationAuditError.context?.status
          if (
            pageLocationAuditError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(pageLocationAuditError.message)
            return
          }
        }
        if (prop?.id) {
          const findings = Array.isArray(pageLocationAuditData?.findings)
            ? pageLocationAuditData.findings
            : []
          const today = new Date().toISOString().slice(0, 10)
          const { data: existingRows } = await supabase
            .from("ga4_page_location_audit_results")
            .select("id, page_location, last_detected")
            .eq("ga4_property_id", prop.id)
          const existingMap = new Map(
            (existingRows || []).map((row) => [row.page_location, row])
          )
          const seenLocations = new Set()
          for (const finding of findings) {
            const pageLocation =
              typeof finding?.pageLocation === "string"
                ? finding.pageLocation
                : null
            if (!pageLocation) continue
            const matches = Array.isArray(finding.matches)
              ? finding.matches
                  .map((match) => sanitizePageLocationMatch(match))
                  .filter(Boolean)
              : []
            const eventCount = normalizeEventCount(finding.eventCount)
            seenLocations.add(pageLocation)
            const existing = existingMap.get(pageLocation)
            if (existing) {
              await supabase
                .from("ga4_page_location_audit_results")
                .update({
                  event_count: eventCount,
                  pii_matches: matches,
                  last_detected: null,
                })
                .eq("id", existing.id)
            } else {
              await supabase.from("ga4_page_location_audit_results").insert({
                ga4_property_id: prop.id,
                page_location: pageLocation,
                first_detected: today,
                event_count: eventCount,
                pii_matches: matches,
              })
            }
          }
          for (const [location, row] of existingMap.entries()) {
            if (!seenLocations.has(location) && !row.last_detected) {
              await supabase
                .from("ga4_page_location_audit_results")
                .update({ last_detected: today })
                .eq("id", row.id)
            }
          }
        }

        if (isAutomotive) {
        setAuditStepProgress(propertyNumber, "ccrReport", displayName)
        const {
          data: ccrData,
          error: ccrError,
        } = await invoke(
          {
            type: "ccr-report",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] CCR report result", pid, {
          data: ccrData,
          error: ccrError,
        })
        if (ccrError) {
          const status = ccrError.context?.status
          if (
            ccrError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(ccrError.message)
            return
          }
        }
        if (prop?.id) {
          await supabase
            .from("ga4_ccr_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          await supabase
            .from("ga4_ccr_car_calculation_sources")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (Array.isArray(ccrData) && ccrData.length) {
            const sourceRows = buildCcrCarCalculationSourceRows(prop.id, ccrData)
            await supabase
              .from("ga4_ccr_results")
              .insert(
                ccrData.map((row) => ({
                  ga4_property_id: prop.id,
                  event_owner: row.eventOwner,
                  product_name: row.productName,
                  event_action: row.eventAction,
                  element_text: row.elementText,
                  event_count: row.eventCount,
                  monthly_breakdown: Array.isArray(row.monthlyBreakdown)
                    ? row.monthlyBreakdown
                    : [],
                  item_breakdown: Array.isArray(row.itemBreakdown)
                    ? row.itemBreakdown
                    : Array.isArray(row.item_breakdown)
                      ? row.item_breakdown
                      : [],
                }))
              )
            if (sourceRows.length) {
              await supabase
                .from("ga4_ccr_car_calculation_sources")
                .insert(sourceRows)
            }
          }
        }
        }

        setAuditStepProgress(propertyNumber, "hostnameAudit", displayName)
        const {
          data: hostnameAuditData,
          error: hostnameAuditError,
        } = await invoke(
          {
            type: "hostname-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Hostname audit result", pid, {
          data: hostnameAuditData,
          error: hostnameAuditError,
        })
        if (hostnameAuditError) {
          const status = hostnameAuditError.context?.status
          if (
            hostnameAuditError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(hostnameAuditError.message)
            return
          }
        }
        const existingHostnameMap = new Map()
        let mergedHostnameEntries = Array.isArray(hostnameAuditData)
          ? [...hostnameAuditData]
          : []

        if (prop?.id && Array.isArray(hostnameAuditData)) {
          const today = new Date().toISOString().slice(0, 10)
          const { data: existingRows } = await supabase
            .from("ga4_hostname_audit_results")
            .select("id, hostname, expected, first_detected, last_detected")
            .eq("ga4_property_id", prop.id)
          for (const row of existingRows || []) {
            if (!row?.hostname) continue
            existingHostnameMap.set(row.hostname, row)
          }
          const existingMap = new Map(existingHostnameMap.entries())
          const seen = new Set()
          for (const h of hostnameAuditData) {
            const host = h.hostname
            const sessions = h.sessions
            const ownerList = Array.isArray(h.eventOwners) ? h.eventOwners : []
            const eventOwners = Array.from(
              new Set(
                ownerList
                  .filter((owner) => typeof owner === "string")
                  .map((owner) => owner.trim())
                  .filter((owner) => owner && owner !== "(not set)")
              )
            )
            const row = existingMap.get(host)
            seen.add(host)
            if (row) {
              await supabase
                .from("ga4_hostname_audit_results")
                .update({
                  session_count: sessions,
                  last_detected: null,
                  event_owners: eventOwners,
                })
                .eq("id", row.id)
            } else {
              await supabase.from("ga4_hostname_audit_results").insert({
                ga4_property_id: prop.id,
                hostname: host,
                first_detected: today,
                session_count: sessions,
                event_owners: eventOwners,
              })
            }
          }
          for (const [host, row] of existingMap.entries()) {
            if (!seen.has(host) && !row.last_detected) {
              await supabase
                .from("ga4_hostname_audit_results")
                .update({ last_detected: today })
                .eq("id", row.id)
            }
          }
          mergedHostnameEntries = hostnameAuditData.map((entry) => {
            const match = existingHostnameMap.get(entry.hostname)
            if (match) {
              return {
                ...entry,
                expected: match.expected,
              }
            }
            return entry
          })
          for (const [host, row] of existingHostnameMap.entries()) {
            if (
              !mergedHostnameEntries.some((entry) => entry?.hostname === host)
            ) {
              mergedHostnameEntries.push({
                hostname: host,
                expected: row?.expected,
              })
            }
          }
        }

        try {
          const accountSyncResult = await syncGa4AccountForProperty({
            property: prop,
            propertyDisplayName: displayName,
            hostnameAuditData,
          })
          if (accountSyncResult?.createdAccount && prop?.organization_id) {
            await reconcileDealershipBilling(prop.organization_id)
          }

          if (
            accountSyncResult?.accountId &&
            accountSyncResult?.updatedProperty
          ) {
            setSavedProperties((prev) =>
              prev.map((p) =>
                p.id === prop.id
                  ? { ...p, account_id: accountSyncResult.accountId }
                  : p,
              ),
            )

            if (accountSyncResult.accountId) {
              prop = { ...prop, account_id: accountSyncResult.accountId }
            }

            if (
              Array.isArray(eventAuditRowsForSync) &&
              eventAuditRowsForSync.length > 0
            ) {
              await applyEventOwnersToAccountProducts({
                accountId: accountSyncResult.accountId,
                organizationId: prop?.organization_id,
                ownerRows: eventAuditRowsForSync,
              })
            }
          }

          const resolvedAccountId =
            accountSyncResult?.accountId || prop?.account_id || null

          try {
            const coverageResult = await evaluateCrossDomainCoverage({
              propertyRecord: prop,
              accountId: resolvedAccountId,
              hostnames: mergedHostnameEntries,
              normalizedMeasurementIds,
            })
            const missingHosts = coverageResult?.missingHosts || []
            if (missingHosts.length) {
              console.warn("[GA4] Missing cross-domain coverage for hostnames", {
                propertyId: prop?.property_id,
                hosts: missingHosts,
              })
              const existingErrors = Array.isArray(data.errors)
                ? [...data.errors]
                : []
              const existingErrorSet = new Set(
                existingErrors
                  .filter((err) => typeof err === "string")
                  .map((err) => err),
              )
              const newMessages = []
              for (const host of missingHosts) {
                const displayHost =
                  typeof host === "string" && host.trim()
                    ? host.trim()
                    : "domain"
                const message = formatCrossDomainActionMessage(displayHost)
                if (!existingErrorSet.has(message)) {
                  existingErrorSet.add(message)
                  newMessages.push(message)
                }
              }
              if (newMessages.length) {
                const nextErrors = [...existingErrors, ...newMessages]
                const updatedData = { ...data, errors: nextErrors }
                const nextTotalIssues = countSettingsAuditIssues(updatedData)
                let updateFailed = false
                if (latestSettingsRowId) {
                  const { error: settingsUpdateError } = await supabase
                    .from("ga4_property_settings")
                    .update({
                      settings: updatedData,
                      issue_count: nextTotalIssues,
                    })
                    .eq("id", latestSettingsRowId)
                  if (settingsUpdateError) {
                    updateFailed = true
                    console.error(
                      "[GA4] Failed to update GA4 settings with cross-domain issues",
                      settingsUpdateError,
                    )
                  }
                }
                if (!updateFailed) {
                  data.errors = nextErrors
                  totalIssueCount = nextTotalIssues
                }
              }
            }
          } catch (coverageError) {
            console.error(
              "[GA4] Failed to evaluate cross-domain coverage",
              coverageError,
            )
          }

          await syncGa4MeasurementScripts({
            propertyRecord: prop,
            accountId: resolvedAccountId,
            hostnames: hostnameAuditData,
            addedUrls: accountSyncResult?.addedWebsiteUrls || [],
            measurementIds,
            measurementScripts,
          })
        } catch (accountSyncError) {
          console.error(
            "[GA4] Failed to synchronize GA4 account after hostname audit",
            {
              propertyId: prop?.property_id,
              propertyRecordId: prop?.id,
              error: accountSyncError,
            },
          )
          setError(
            accountSyncError?.message ||
              "Failed to synchronize GA4 account assignment. Please try again.",
          )
          return
        }

        setAuditStepProgress(propertyNumber, "channelAudit", displayName)
        const {
          data: channelAuditData,
          error: channelAuditError,
        } = await invoke(
          {
            type: "channel-grouping-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Channel grouping audit result", pid, {
          data: channelAuditData,
          error: channelAuditError,
        })
        if (channelAuditError) {
          const status = channelAuditError.context?.status
          if (
            channelAuditError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(channelAuditError.message)
            return
          }
        }
        if (prop?.id && Array.isArray(channelAuditData)) {
          await supabase
            .from("ga4_channel_grouping_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (channelAuditData.length) {
            await supabase
              .from("ga4_channel_grouping_audit_results")
              .insert(
                channelAuditData.map((r) => ({
                  ga4_property_id: prop.id,
                  channel_group: r.channelGroup,
                  source: r.source,
                  medium: r.medium,
                  session_count: r.sessions,
                }))
              )
          }
        }

        if (prop?.id) {
          const {
            data: channelTemplateAuditData,
            error: channelTemplateAuditError,
          } = await invoke(
            {
              type: "channel-grouping-template-audit",
              propertyId: normalizePropertyId(pid),
              propertyRecordId: prop.id,
            },
            { skipLoading: true, tokenUserId }
          )
          console.log("[GA4] Channel grouping template audit result", pid, {
            data: channelTemplateAuditData,
            error: channelTemplateAuditError,
          })
          if (channelTemplateAuditError) {
            const status = channelTemplateAuditError.context?.status
            if (
              channelTemplateAuditError.message.includes(
                "Missing Google access token"
              ) ||
              status === 401
            ) {
              setError(AUTH_ERROR_MSG)
              return
            } else {
              setError(channelTemplateAuditError.message)
              return
            }
          }
          await supabase
            .from("ga4_channel_group_template_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (Array.isArray(channelTemplateAuditData) && channelTemplateAuditData.length) {
            await supabase
              .from("ga4_channel_group_template_audit_results")
              .insert(
                channelTemplateAuditData.map((issue) => ({
                  ga4_property_id: prop.id,
                  issue_type: issue.issueType,
                  channel_group_name: issue.channelGroupName,
                  rule_display_name: issue.ruleDisplayName,
                  rule_index:
                    typeof issue.ruleIndex === "number" && Number.isFinite(issue.ruleIndex)
                      ? issue.ruleIndex
                      : null,
                  expected_rule:
                    issue.expectedRule && typeof issue.expectedRule === "object"
                      ? issue.expectedRule
                      : null,
                  actual_rule:
                    issue.actualRule && typeof issue.actualRule === "object"
                      ? issue.actualRule
                      : null,
                }))
              )
          }
        }

        setAuditStepProgress(propertyNumber, "selfReferralAudit", displayName)
        const {
          data: selfReferralAuditData,
          error: selfReferralAuditError,
        } = await invoke(
          {
            type: "self-referral-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Self referral audit result", pid, {
          data: selfReferralAuditData,
          error: selfReferralAuditError,
        })
        if (selfReferralAuditError) {
          const status = selfReferralAuditError.context?.status
          if (
            selfReferralAuditError.message.includes(
              "Missing Google access token"
            ) ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(selfReferralAuditError.message)
            return
          }
        }
        if (prop?.id && Array.isArray(selfReferralAuditData)) {
          await supabase
            .from("ga4_self_referral_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)
          const rows = selfReferralAuditData
            .map((row) => {
              const source =
                typeof row?.source === "string" && row.source.trim()
                  ? row.source.trim()
                  : "(not set)"
              const medium =
                typeof row?.medium === "string" && row.medium.trim()
                  ? row.medium.trim()
                  : "(not set)"
              const sessionsValue = Number(row?.sessions)
              const sessionCount = Number.isFinite(sessionsValue)
                ? Math.round(sessionsValue)
                : 0
              const matchedHostname =
                typeof row?.matchedHostname === "string" &&
                row.matchedHostname.trim()
                  ? row.matchedHostname.trim()
                  : null
              const matchSources = Array.isArray(row?.matchSources)
                ? Array.from(
                    new Set(
                      row.matchSources
                        .map((value) =>
                          typeof value === "string" && value.trim()
                            ? value.trim()
                            : null
                        )
                        .filter((value) => value)
                    )
                  )
                : []
              return {
                ga4_property_id: prop.id,
                source,
                medium,
                session_count: sessionCount,
                matched_hostname: matchedHostname,
                match_sources: matchSources,
              }
            })
            .filter((row) => row.session_count > 0)
          if (rows.length) {
            await supabase
              .from("ga4_self_referral_audit_results")
              .insert(rows)
          }
        }

        setAuditStepProgress(propertyNumber, "googleAds", displayName)
        const {
          data: googleAdsAuditData,
          error: googleAdsAuditError,
        } = await invoke(
          {
            type: "google-ads-audit",
            propertyId: normalizePropertyId(pid),
          },
          { skipLoading: true, tokenUserId }
        )
        console.log("[GA4] Google Ads audit result", pid, {
          data: googleAdsAuditData,
          error: googleAdsAuditError,
        })
        if (googleAdsAuditError) {
          const status = googleAdsAuditError.context?.status
          if (
            googleAdsAuditError.message.includes("Missing Google access token") ||
            status === 401
          ) {
            setError(AUTH_ERROR_MSG)
            return
          } else {
            setError(googleAdsAuditError.message)
            return
          }
        }

        if (prop?.id) {
          await supabase
            .from("ga4_google_ads_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)

          const googleAdsLinks = Array.isArray(
            googleAdsAuditData?.links
          )
            ? googleAdsAuditData.links || []
            : Array.isArray(googleAdsAuditData)
            ? googleAdsAuditData
            : []

          const normalizeLinkValue = (value) => {
            if (typeof value !== "string") return null
            const trimmed = value.trim()
            return trimmed ? trimmed : null
          }

          const parseCostNumber = (value) => {
            if (typeof value === "number") {
              return Number.isFinite(value) ? value : null
            }
            if (typeof value === "string") {
              const trimmed = value.trim()
              if (!trimmed) return null
              const num = Number(trimmed)
              return Number.isFinite(num) ? num : null
            }
            return null
          }

          const normalizeDateValue = (value) => {
            if (typeof value !== "string") return null
            const trimmed = value.trim()
            if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null
            return trimmed
          }

          const googleAdsRows = googleAdsLinks
            .map((link) => ({
              ga4_property_id: prop.id,
              link_resource_name: normalizeLinkValue(link?.linkName),
              customer_id: normalizeLinkValue(link?.customerId),
              creator_email: normalizeLinkValue(link?.creatorEmailAddress),
              ads_personalization_enabled:
                typeof link?.adsPersonalizationEnabled === "boolean"
                  ? Boolean(link.adsPersonalizationEnabled)
                  : null,
            }))
            .filter((row) => row != null)

          if (googleAdsRows.length) {
            await supabase
              .from("ga4_google_ads_audit_results")
              .insert(googleAdsRows)
          }

          const googleAdsCosts = Array.isArray(googleAdsAuditData?.costs)
            ? googleAdsAuditData.costs
            : []
          const costDateRange = googleAdsAuditData?.costDateRange || null
          const defaultCurrency = normalizeLinkValue(
            googleAdsAuditData?.currencyCode,
          )

          const googleAdsCostRows = googleAdsCosts
            .map((entry) => {
              const customerId = normalizeLinkValue(
                entry?.googleAdsCustomerId ?? entry?.customerId,
              )
              if (!customerId) return null
              const advertiserCost = parseCostNumber(
                entry?.advertiserAdCost ?? entry?.cost,
              )
              const currency =
                normalizeLinkValue(entry?.currencyCode) || defaultCurrency || null
              const periodStart =
                normalizeDateValue(entry?.cost_period_start) ||
                normalizeDateValue(entry?.startDate) ||
                normalizeDateValue(costDateRange?.startDate) ||
                null
              const periodEnd =
                normalizeDateValue(entry?.cost_period_end) ||
                normalizeDateValue(entry?.endDate) ||
                normalizeDateValue(costDateRange?.endDate) ||
                null
              return {
                ga4_property_id: prop.id,
                google_ads_customer_id: customerId,
                advertiser_ad_cost: advertiserCost,
                currency_code: currency,
                cost_period_start: periodStart,
                cost_period_end: periodEnd,
              }
            })
            .filter((row) => row != null)

          await supabase
            .from("ga4_google_ads_cost_audit_results")
            .delete()
            .eq("ga4_property_id", prop.id)

          if (googleAdsCostRows.length) {
            await supabase
              .from("ga4_google_ads_cost_audit_results")
              .insert(googleAdsCostRows)
          }
        }

        if (prop?.template_id) {
          const tpl = templates.find((t) => t.id === prop.template_id)
          const cmp = compareWithTemplate(tpl?.template, data, eventsData)
          const { error: mismatchesDeleteError } = await supabase
            .from("ga4_property_template_mismatches")
            .delete()
            .eq("ga4_property_id", prop.id)
          if (mismatchesDeleteError) {
            console.error(
              "[GA4] Failed to clear previous GA4 template mismatches",
              mismatchesDeleteError,
            )
          }
          const { error: mismatchError } = await supabase
            .from("ga4_property_template_mismatches")
            .insert({ ga4_property_id: prop.id, mismatches: cmp })
            .select("id, created_at")
            .single()
          if (mismatchError) {
            console.error("[GA4] Failed to store template mismatches", mismatchError)
          } else {
            await pruneHistoricalAuditRows("ga4_property_template_mismatches", prop.id)
          }
          const mismatchIssueCount = countTemplateMismatchIssues(cmp)
          if (mismatchIssueCount > 0) {
            const nextTotalIssues = totalIssueCount + mismatchIssueCount
            if (latestSettingsRowId) {
              const { error: issueUpdateError } = await supabase
                .from("ga4_property_settings")
                .update({ issue_count: nextTotalIssues })
                .eq("id", latestSettingsRowId)
              if (issueUpdateError) {
                console.error(
                  "[GA4] Failed to update GA4 settings issue count",
                  issueUpdateError,
                )
              } else {
                totalIssueCount = nextTotalIssues
              }
            } else {
              totalIssueCount = nextTotalIssues
            }
          }
        }
      }

      updateFunctionLoading({
        message: "Running GA4 audit…",
        details: "Finalizing audit results…",
        progress: {
          currentItem: totalProperties,
          totalItems: totalProperties,
          currentStep: totalSteps,
          totalSteps,
          stepLabel: "Finalizing results",
        },
      })

      await loadSavedProperties(targetPropertyIds)

      updateFunctionLoading({
        message: "GA4 audit complete",
        details:
          totalProperties === 1
            ? "Latest results saved to Supabase."
            : "Latest results saved for all selected properties.",
        progress: {
          currentItem: totalProperties,
          totalItems: totalProperties,
          currentStep: totalSteps,
          totalSteps,
          stepLabel: "Complete",
        },
        status: "complete",
      })

      await new Promise((resolve) => setTimeout(resolve, 600))
    } catch (e) {
      console.error("[GA4] Settings audit failed", e)
      setError(e.message)
    } finally {
      setFunctionLoading(null)
    }
  }

  const filteredProps = useMemo(() => {
    const accountTerm = modalAccountFilter.trim().toLowerCase()
    const propertyTerm = modalPropertyFilter.trim().toLowerCase()
    if (!accountTerm && !propertyTerm) return availableProps
    return availableProps.filter((p) => {
      const accountName = (
        p.accountDisplayName || p.accountLabel || p.account || ""
      ).toLowerCase()
      const accountId = (p.account || "").toLowerCase()
      const propertyName = (p.displayName || "").toLowerCase()
      const propertyId = (p.property || "").toLowerCase()
      const matchesAccount = accountTerm
        ? accountName.includes(accountTerm) || accountId.includes(accountTerm)
        : true
      const matchesProperty = propertyTerm
        ? propertyName.includes(propertyTerm) || propertyId.includes(propertyTerm)
        : true
      return matchesAccount && matchesProperty
    })
  }, [availableProps, modalAccountFilter, modalPropertyFilter])

  const filteredPropertyIds = useMemo(
    () => filteredProps.map((p) => p.property),
    [filteredProps]
  )

  const modalAllFilteredSelected =
    filteredPropertyIds.length > 0 &&
    filteredPropertyIds.every((id) => selectedNewProps.includes(id))

  const toggleSelectFiltered = () => {
    if (filteredPropertyIds.length === 0) return
    setSelectedNewProps((prev) => {
      if (modalAllFilteredSelected) {
        const filteredSet = new Set(filteredPropertyIds)
        return prev.filter((id) => !filteredSet.has(id))
      }
      const next = new Set(prev)
      filteredPropertyIds.forEach((id) => next.add(id))
      return Array.from(next)
    })
  }

  return (
    <>
      {functionLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            style={{
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "2rem 2.5rem",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--primary-color)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
              textAlign: "center",
              minWidth: "280px",
              maxWidth: "420px",
            }}
          >
            {functionLoading.status === "complete" ? (
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Completed"
                style={{
                  marginBottom: "1rem",
                  color: "var(--primary-color)",
                  fill: "var(--primary-color)",
                }}
              >
                <path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm4.707-10.707-5 5a1 1 0 0 1-1.414 0l-2-2 1.414-1.414L11 12.586l4.293-4.293Z" />
              </svg>
            ) : (
              <svg
                width="60"
                height="60"
                viewBox="0 0 50 50"
                role="img"
                aria-label="Loading"
                style={{ marginBottom: "1rem" }}
              >
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="var(--primary-color)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="31.4 31.4"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 25 25"
                    to="360 25 25"
                    dur="0.9s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            )}
            <h3 style={{ marginBottom: "0.75rem" }}>
              {functionLoading.message || "Processing request…"}
            </h3>
            {functionLoading.details && (
              <p style={{ margin: "0.25rem 0" }}>{functionLoading.details}</p>
            )}
            {functionLoading.progress?.totalItems ? (
              <p style={{ margin: "0.25rem 0" }}>
                {functionLoading.progress.currentItem > 0
                  ? `Property ${Math.min(
                      functionLoading.progress.currentItem,
                      functionLoading.progress.totalItems
                    )} of ${functionLoading.progress.totalItems}`
                  : `Total properties: ${functionLoading.progress.totalItems}`}
              </p>
            ) : null}
            {functionLoading.progress?.totalSteps &&
            (functionLoading.progress.stepLabel ||
              functionLoading.progress.currentStep > 0) ? (
              <p style={{ margin: "0.25rem 0" }}>
                {functionLoading.progress.currentStep > 0
                  ? `Step ${Math.min(
                      functionLoading.progress.currentStep,
                      functionLoading.progress.totalSteps
                    )} of ${functionLoading.progress.totalSteps}${
                      functionLoading.progress.stepLabel
                        ? `: ${functionLoading.progress.stepLabel}`
                        : ""
                    }`
                  : functionLoading.progress.stepLabel}
              </p>
            ) : null}
          </div>
        </div>
      )}
      {listingProperties && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
          }}
        >
          <div
            style={{
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "2rem 2.5rem",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--primary-color)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
              textAlign: "center",
              minWidth: "260px",
            }}
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 50 50"
              role="img"
              aria-label="Loading"
              style={{ marginBottom: "1rem" }}
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="var(--primary-color)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="31.4 31.4"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.9s"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <h3 style={{ marginBottom: "0.75rem" }}>Listing GA4 properties…</h3>
            <p style={{ margin: "0.25rem 0" }}>
              Accounts processed: {listingProgress?.accountsProcessed ?? 0}
              {typeof listingProgress?.totalAccounts === "number"
                ? ` / ${listingProgress.totalAccounts}`
                : ""}
            </p>
            <p style={{ margin: "0.25rem 0" }}>
              Properties fetched: {listingProgress?.propertiesFetched ?? 0}
              {typeof listingProgress?.totalProperties === "number"
                ? ` / ${listingProgress.totalProperties}`
                : ""}
            </p>
            {listingProgress?.currentAccount && (
              <p
                style={{
                  margin: "0.5rem 0 0",
                  fontSize: "0.9rem",
                  color: "var(--primary-color)",
                }}
              >
                Current account: {listingProgress.currentAccount}
              </p>
            )}
            <button
              type="button"
              onClick={cancelListing}
              style={{
                marginTop: "1.25rem",
                padding: "0.5rem 1.25rem",
                borderRadius: "var(--border-radius)",
                border: "1px solid var(--primary-color)",
                backgroundColor: "transparent",
                color: "var(--primary-color)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="page-container page-container--wide">
        <GA4AuditHeader
          ga4Enabled={ga4Enabled}
          advancedMode={advancedMode}
          setAdvancedMode={setAdvancedMode}
          advancedDataset={advancedDataset}
          setAdvancedDataset={setAdvancedDataset}
          handleAdvancedEditClick={handleAdvancedEditClick}
          tableSearchTerm={tableSearchTerm}
          setTableSearchTerm={setTableSearchTerm}
          onManageAccess={handleManageAccessClick}
          onViewTemplates={handleViewTemplatesClick}
          automotiveFeaturesEnabled={isAutomotive}
        />
        {advancedMode &&
          advancedDataset === "audits" &&
          editSelectionError && (
            <p
              style={{
                color: "#b91c1c",
                marginTop: "0.5rem",
                fontWeight: 500,
              }}
            >
              {editSelectionError}
            </p>
          )}
        {error && (
          <div
            style={{ color: "red", marginTop: "0.5rem", marginBottom: "1rem" }}
            role="alert"
          >
            <span>{error}</span>
            {error === AUTH_ERROR_MSG && (
              <button
                onClick={() => refreshGoogleAuth()}
                style={{ marginLeft: "0.5rem" }}
              >
                Refresh OAuth Access
              </button>
            )}
          </div>
        )}
        {advancedMode && advancedLoading && (
          <p style={{ marginTop: "0.5rem" }}>Loading advanced data…</p>
        )}
        {advancedMode &&
          !advancedLoading &&
          activeAdvancedColumns.length === 0 && (
            <p style={{ marginTop: "0.5rem" }}>
              {advancedDataset === "audits"
                ? "No audit settings found for the selected properties."
                : "No performance data found for the selected properties."}
            </p>
          )}
      <div style={{ marginBottom: "1rem" }}>
        <Button onClick={checkNewAccounts} size="sm">
          Check for New Accounts
        </Button>
        <Button
          onClick={() => setOvernightDialogOpen(true)}
          variant="outline"
          size="sm"
          style={{ marginLeft: "0.5rem" }}
          disabled={!selectedOrgId}
        >
          Manage Overnight Audits
        </Button>
        {selectedProps.length > 0 && (
          <>
            <button onClick={runSettingsAudit} style={{ marginLeft: "0.5rem" }}>
              Run Audit
            </button>
            <button
              onClick={openAssignTemplateModal}
              style={{ marginLeft: "0.5rem" }}
            >
              Assign Templates
            </button>
            <Button
              onClick={deleteSelectedProperties}
              variant="destructive"
              size="sm"
              style={{ marginLeft: "0.5rem" }}
            >
              Delete
            </Button>
          </>
        )}
      </div>
      {savedProperties.length > 0 && (
        <div style={{ marginTop: "1rem", overflowX: "auto" }}>
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              border: "1px solid var(--border-color, #e2e8f0)",
              borderRadius: "var(--border-radius)",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
            }}
          >
            <table className="ui-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: advancedMode ? "780px" : "680px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "center",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={handleToggleSelectAll}
                      disabled={filteredProperties.length === 0}
                      aria-label="Select all filtered properties"
                    />
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Property Name
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Property ID
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Issues
                  </th>
                  {advancedMode &&
                    activeAdvancedColumns.map((column) => (
                      <th
                        key={`advanced-header-${column.key}`}
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#1e3a8a",
                          color: "white",
                          zIndex: 2,
                          textAlign: "left",
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Linked By
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Template
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Last Audit
                  </th>
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#1e3a8a",
                      color: "white",
                      zIndex: 2,
                      textAlign: "left",
                      padding: "0.5rem 0.75rem",
                      borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProperties.map((p) => {
                  const rowStyle = newIds.includes(p.property_id)
                    ? { backgroundColor: "#065f46", color: "white" }
                    : auditSaved[p.property_id]
                    ? { backgroundColor: "#1e40af", color: "white" }
                    : {}
                  const auditEntry = auditDataMap[p.property_id] || null
                  const auditValues = auditEntry?.values || {}
                  const auditSettings = auditEntry?.settings || {}
                  const performanceValues = performanceDataMap[p.property_id] || {}
                  const issueCount =
                    typeof p.issue_count === "number" ? p.issue_count : 0
                  const hasIssues = issueCount > 0
                  const issueLabel = hasIssues
                    ? `${issueCount} open ${issueCount === 1 ? "issue" : "issues"}`
                    : "No issues detected"
                  const templateRecord =
                    p.template_id != null ? templateMap.get(p.template_id) : null
                  const propertyType =
                    typeof auditSettings?.propertyType === "string"
                      ? auditSettings.propertyType
                      : null
                  const propertyTypeMap =
                    templateRecord?.template?.propertyTypes || null
                  const templateSettingsByType = propertyTypeMap
                    ? propertyTypeMap?.[propertyType] ||
                      propertyTypeMap?.PROPERTY_TYPE_ORDINARY ||
                      Object.values(propertyTypeMap).find(
                        (value) => value && typeof value === "object"
                      ) ||
                      null
                    : null
                  const rowHasHighlight = rowStyle?.color === "white"
                  return (
                    <tr key={p.id} style={rowStyle}>
                      <td
                        style={{
                          textAlign: "center",
                          cursor: "pointer",
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                        }}
                        onClick={() => togglePropertySelection(p.property_id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProps.includes(p.property_id)}
                          onChange={() => togglePropertySelection(p.property_id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select property ${p.property_id}`}
                        />
                      </td>
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          verticalAlign: "top",
                        }}
                      >
                        {p.property_name}
                        {newIds.includes(p.property_id) && (
                          <span
                            style={{
                              marginLeft: "0.5rem",
                              backgroundColor: "#134e4a",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              display: "inline-block",
                            }}
                          >
                            New Account Found
                          </span>
                        )}
                        {auditSaved[p.property_id] && (
                          <span
                            style={{
                              marginLeft: "0.5rem",
                              backgroundColor: "#1e3a8a",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "0.75rem",
                              display: "inline-block",
                            }}
                          >
                            Audit Saved
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.property_id}
                      </td>
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          whiteSpace: "nowrap",
                          verticalAlign: "top",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.35rem",
                            backgroundColor: hasIssues ? "#b91c1c" : "#1f2937",
                            color: "white",
                            borderRadius: "999px",
                            padding: "0.15rem 0.6rem",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                          title={issueLabel}
                        >
                          <span>{issueCount}</span>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Issues
                          </span>
                        </span>
                      </td>
                      {advancedMode &&
                        activeAdvancedColumns.map((column) => {
                          const auditValue =
                            advancedDataset === "audits"
                              ? auditValues[column.key]
                              : null
                          const displayValue =
                            advancedDataset === "audits"
                              ? formatAuditDisplayValue(auditValue)
                              : formatPerformanceCellValue(
                                  performanceValues?.[column.key]
                                )
                          let mismatch = false
                          let cellTitle
                          if (advancedDataset === "audits" && templateSettingsByType) {
                            const templateConfig =
                              templateSettingsByType?.[column.key] ?? null
                            let expectedValue = null
                            let hasExpectation = false
                            let ignoreMatch = false
                            if (
                              templateConfig &&
                              typeof templateConfig === "object" &&
                              Object.prototype.hasOwnProperty.call(
                                templateConfig,
                                "value"
                              )
                            ) {
                              ignoreMatch = templateConfig.action === "ignore"
                              if (!ignoreMatch) {
                                expectedValue = templateConfig.value
                                hasExpectation = true
                              }
                            } else if (templateConfig !== undefined) {
                              expectedValue = templateConfig
                              hasExpectation = true
                            }
                            if (hasExpectation) {
                              const actualComparable = toComparableValue(auditValue)
                              const expectedComparable = toComparableValue(
                                expectedValue
                              )
                              mismatch = actualComparable !== expectedComparable
                              if (mismatch) {
                                cellTitle = `Expected: ${formatAuditDisplayValue(
                                  expectedValue
                                )}`
                              }
                            }
                          }
                          const mismatchStyle = mismatch
                            ? {
                                backgroundColor: "rgba(248, 113, 113, 0.2)",
                                color: rowHasHighlight ? "#111827" : undefined,
                                fontWeight: 600,
                              }
                            : {}
                          return (
                            <td
                              key={`${p.property_id}-${column.key}`}
                              title={cellTitle}
                              style={{
                                padding: "0.5rem 0.75rem",
                                borderBottom:
                                  "1px solid var(--border-color, #e2e8f0)",
                                verticalAlign: "top",
                                ...mismatchStyle,
                              }}
                            >
                              {displayValue}
                            </td>
                          )
                        })}
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.google_account_email || ""}
                      </td>
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                        }}
                      >
                        <select
                          value={p.template_id || ""}
                          onChange={(e) =>
                            updateTemplate(p.property_id, e.target.value)
                          }
                        >
                          <option value="">No Template</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td
                        style={{
                          fontSize: "0.875rem",
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.last_audit
                          ? new Date(p.last_audit).toLocaleString()
                          : "Never"}
                      </td>
                      <td
                        style={{
                          padding: "0.5rem 0.75rem",
                          borderBottom: "1px solid var(--border-color, #e2e8f0)",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button
                          onClick={() => navigate(`/ga4-property?id=${p.property_id}`)}
                        >
                          View Property
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {assignTemplateModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: "1rem",
          }}
          onClick={closeAssignTemplateModal}
        >
          <div
            style={{
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "1.75rem",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--primary-color)",
              boxShadow: "0 18px 36px rgba(0,0,0,0.45)",
              width: "min(480px, 90vw)",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0 }}>Assign Template</h3>
            <p style={{ margin: 0 }}>
              Selected {selectedProps.length} GA4 properties.
            </p>
            <label style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <span style={{ fontWeight: 500 }}>Template</span>
              <select
                value={assignTemplateId}
                onChange={(e) => setAssignTemplateId(e.target.value)}
                disabled={assignTemplateLoading}
                style={{ padding: "0.5rem", borderRadius: "var(--border-radius)" }}
              >
                <option value="">No Template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            {assignTemplateError && (
              <p style={{ color: "#b91c1c", margin: 0 }}>{assignTemplateError}</p>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
              }}
            >
              <button
                onClick={assignTemplateToSelected}
                disabled={assignTemplateLoading}
              >
                {assignTemplateLoading ? "Assigning…" : "Assign"}
              </button>
              <button
                onClick={closeAssignTemplateModal}
                disabled={assignTemplateLoading}
                style={{ backgroundColor: "transparent", border: "1px solid var(--border-color, #e2e8f0)" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showSelectModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "2rem",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--primary-color)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
              width: "min(1000px, 95vw)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            <Button
              onClick={() => setShowSelectModal(false)}
              variant="ghost"
              size="sm"
              style={{
                position: "absolute",
                top: "0.75rem",
                right: "0.75rem",
                fontSize: "1.75rem",
                lineHeight: 1,
                padding: 0,
                width: "2rem",
                height: "2rem",
                minWidth: "auto",
              }}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </Button>
            <div>
              <h3 style={{ margin: 0 }}>Select GA4 Properties</h3>
              {lastListingTotals && (
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.95rem",
                    color: "var(--text-muted-color, var(--text-color))",
                  }}
                >
                  <div>
                    Accounts processed: {lastListingTotals.accountsProcessed} / {lastListingTotals.totalAccounts}
                  </div>
                  <div>
                    Properties fetched: {lastListingTotals.propertiesFetched}
                    {typeof lastListingTotals.totalProperties === "number"
                      ? ` / ${lastListingTotals.totalProperties}`
                      : ""}
                  </div>
                </div>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "0.75rem",
                alignItems: "end",
              }}
            >
              {selectedOrgId && (
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.35rem",
                  }}
                >
                  <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    Assign to account (optional)
                  </span>
                  <select
                    value={selectedAccountId}
                    onChange={(e) => {
                      setSelectedAccountId(e.target.value)
                    }}
                    disabled={
                      accountPickerLoading || accountOptions.length === 0
                    }
                    data-testid="ga4-account-select"
                    style={{
                      padding: "0.5rem",
                      borderRadius: "var(--border-radius)",
                      border: "1px solid var(--border-color, #ccc)",
                    }}
                  >
                    <option value="">
                      {accountPickerLoading
                        ? "Loading accounts…"
                        : "Select an account"}
                    </option>
                    {accountOptions.map((acct) => (
                      <option key={acct.id} value={acct.id}>
                        {acct.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Filter by account</span>
                <input
                  type="text"
                  value={modalAccountFilter}
                  onChange={(e) => setModalAccountFilter(e.target.value)}
                  placeholder="Search by account name or ID"
                  style={{ padding: "0.5rem", borderRadius: "var(--border-radius)", border: "1px solid var(--border-color, #ccc)" }}
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Filter by property</span>
                <input
                  type="text"
                  value={modalPropertyFilter}
                  onChange={(e) => setModalPropertyFilter(e.target.value)}
                  placeholder="Search by property name or ID"
                  style={{ padding: "0.5rem", borderRadius: "var(--border-radius)", border: "1px solid var(--border-color, #ccc)" }}
                />
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  type="button"
                  onClick={toggleSelectFiltered}
                  disabled={filteredPropertyIds.length === 0}
                  variant={modalAllFilteredSelected ? "secondary" : "outline"}
                  size="sm"
                >
                  {modalAllFilteredSelected
                    ? "Unselect Filtered"
                    : `Select Filtered (${filteredPropertyIds.length})`}
                </Button>
                <Button
                  onClick={addSelectedProperties}
                  disabled={selectedNewProps.length === 0}
                  variant="primary"
                  size="sm"
                >
                  Add and Run Audit ({selectedNewProps.length})
                </Button>
              </div>
            </div>
            <div
              style={{
                border: "1px solid var(--border-color, #ccc)",
                borderRadius: "var(--border-radius)",
                overflow: "hidden",
                flex: 1,
                minHeight: "200px",
                display: "flex",
              }}
            >
              {filteredProps.length > 0 ? (
                <div style={{ width: "100%", overflow: "auto" }}>
                  <table className="ui-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          backgroundColor: "var(--table-header-bg, rgba(59,130,246,0.12))",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <th style={{ textAlign: "left", padding: "0.75rem", width: "50px" }}>
                          <input
                            type="checkbox"
                            checked={modalAllFilteredSelected}
                            onChange={toggleSelectFiltered}
                            disabled={filteredPropertyIds.length === 0}
                            aria-label="Select all filtered properties"
                          />
                        </th>
                        <th style={{ textAlign: "left", padding: "0.75rem" }}>Account</th>
                        <th style={{ textAlign: "left", padding: "0.75rem" }}>Property Name</th>
                        <th style={{ textAlign: "left", padding: "0.75rem" }}>Property ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProps.map((p) => {
                        const isSelected = selectedNewProps.includes(p.property)
                        const accountDisplay = p.accountLabel || p.accountDisplayName || p.account || ""
                        return (
                          <tr
                            key={p.property}
                            onClick={() =>
                              setSelectedNewProps((prev) =>
                                prev.includes(p.property)
                                  ? prev.filter((id) => id !== p.property)
                                  : [...prev, p.property]
                              )
                            }
                            style={{
                              cursor: "pointer",
                              backgroundColor: isSelected
                                ? "rgba(59,130,246,0.15)"
                                : "transparent",
                            }}
                          >
                            <td style={{ padding: "0.75rem" }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  setSelectedNewProps((prev) =>
                                    prev.includes(p.property)
                                      ? prev.filter((id) => id !== p.property)
                                      : [...prev, p.property]
                                  )
                                }}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select property ${p.property}`}
                              />
                            </td>
                            <td style={{ padding: "0.75rem", whiteSpace: "nowrap" }}>
                              {accountDisplay}
                            </td>
                            <td style={{ padding: "0.75rem" }}>{p.displayName}</td>
                            <td style={{ padding: "0.75rem", whiteSpace: "nowrap" }}>{p.property}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1rem",
                    textAlign: "center",
                  }}
                >
                  <p style={{ margin: 0 }}>No properties match the current filters.</p>
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: "0.9rem" }}>
                Selected {selectedNewProps.length} of {availableProps.length} properties
              </div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Button
                  onClick={() => {
                    setSelectedNewProps([])
                  }}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={selectedNewProps.length === 0}
                >
                  Clear Selection
                </Button>
                <Button
                  onClick={() => setShowSelectModal(false)}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addSelectedProperties}
                  disabled={selectedNewProps.length === 0}
                  variant="primary"
                  size="sm"
                >
                  Add and Run Audit ({selectedNewProps.length})
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
            padding: "1rem",
          }}
        >
          <div
            style={{
              position: "relative",
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "1.25rem",
              maxHeight: "85%",
              overflowY: "auto",
              width: "min(900px, 95%)",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--primary-color)",
              boxShadow: "0 15px 35px rgba(15, 23, 42, 0.2)",
            }}
          >
            <button
              onClick={closeEditModal}
              style={{
                position: "absolute",
                top: "0.75rem",
                right: "0.75rem",
                background: "transparent",
                border: "none",
                color: "var(--primary-color)",
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              &times;
            </button>
            <h3 style={{ marginTop: 0 }}>Edit GA4 Settings</h3>
            <p style={{ marginBottom: "0.25rem" }}>
              <strong>Property:</strong>{" "}
              {editModalProperty?.property_name || "Unnamed"}
              {editModalProperty?.property_id && (
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9rem",
                    marginLeft: "0.5rem",
                    color: "#475569",
                  }}
                >
                  ({editModalProperty.property_id})
                </span>
              )}
            </p>
            {editModalTemplateName && (
              <p style={{ marginTop: 0 }}>
                <strong>Template:</strong> {editModalTemplateName}
              </p>
            )}
            {editModalError && (
              <p style={{ color: "red", marginTop: "0.5rem" }}>
                {editModalError}
              </p>
            )}
            {editModalLoading ? (
              <p style={{ marginTop: "0.75rem" }}>
                Loading current GA4 settings…
              </p>
            ) : (
              <form onSubmit={handleEditModalSave}>
                <table className="ui-table"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: "0.75rem",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "0.5rem 0.75rem",
                          borderBottom:
                            "1px solid var(--border-color, #e2e8f0)",
                        }}
                      >
                        Setting
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "0.5rem 0.75rem",
                          borderBottom:
                            "1px solid var(--border-color, #e2e8f0)",
                        }}
                      >
                        Current Value
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "0.5rem 0.75rem",
                          borderBottom:
                            "1px solid var(--border-color, #e2e8f0)",
                        }}
                      >
                        Template Value
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "0.5rem 0.75rem",
                          borderBottom:
                            "1px solid var(--border-color, #e2e8f0)",
                        }}
                      >
                        Update
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ADVANCED_AUDIT_FIELD_KEYS.map((key) => {
                      const config = ADVANCED_FIELD_CONFIG[key] || {
                        editable: false,
                      }
                      const actual =
                        key === "resetUserDataOnNewActivity"
                          ? editModalSettings?.resetUserDataOnNewActivity
                          : editModalSettings?.[key]
                      let expectedValue = null
                      let hasExpectation = false
                      let ignoreMatch = false
                      const templateConfig =
                        editModalTemplateSettings?.[key] ?? undefined
                      if (
                        templateConfig &&
                        typeof templateConfig === "object" &&
                        Object.prototype.hasOwnProperty.call(
                          templateConfig,
                          "value"
                        )
                      ) {
                        ignoreMatch = templateConfig.action === "ignore"
                        if (!ignoreMatch) {
                          expectedValue = templateConfig.value
                          hasExpectation = true
                        }
                      } else if (templateConfig !== undefined) {
                        expectedValue = templateConfig
                        hasExpectation = true
                      }
                      const actualComparable = toComparableValue(actual)
                      const expectedComparable = toComparableValue(
                        expectedValue
                      )
                      const mismatch =
                        hasExpectation && !ignoreMatch &&
                        actualComparable !== expectedComparable
                      const rowStyle = mismatch
                        ? {
                            backgroundColor: "rgba(248, 113, 113, 0.16)",
                            fontWeight: 600,
                          }
                        : {}
                      const displayActual = formatAuditDisplayValue(actual)
                      const displayExpected = hasExpectation
                        ? formatAuditDisplayValue(expectedValue)
                        : "—"
                      return (
                        <tr key={key} style={rowStyle}>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "0.5rem 0.75rem",
                              borderBottom:
                                "1px solid var(--border-color, #e2e8f0)",
                              verticalAlign: "top",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatColumnLabel(key)}
                          </th>
                          <td
                            style={{
                              padding: "0.5rem 0.75rem",
                              borderBottom:
                                "1px solid var(--border-color, #e2e8f0)",
                              verticalAlign: "top",
                            }}
                          >
                            {displayActual}
                          </td>
                          <td
                            style={{
                              padding: "0.5rem 0.75rem",
                              borderBottom:
                                "1px solid var(--border-color, #e2e8f0)",
                              verticalAlign: "top",
                            }}
                          >
                            {displayExpected}
                          </td>
                          <td
                            style={{
                              padding: "0.5rem 0.75rem",
                              borderBottom:
                                "1px solid var(--border-color, #e2e8f0)",
                              verticalAlign: "top",
                            }}
                          >
                            {config.editable ? (
                              renderEditFieldInput(key, editModalSaving)
                            ) : (
                              <span style={{ color: "#64748b" }}>
                                Not editable in this view
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: "1rem" }}>
                  <div>
                    <strong>GA4 edit access:</strong>{" "}
                    <span
                      style={{
                        color: hasValidEditToken ? "#047857" : "#b45309",
                      }}
                    >
                      {hasValidEditToken
                        ? "Authorized"
                        : "Authorization required"}
                    </span>
                  </div>
                  {!hasValidEditToken && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <button
                        type="button"
                        onClick={startEditAuthFlow}
                        disabled={editModalSaving || editAuthPending}
                      >
                        Authorize GA4 Edits
                      </button>
                    </div>
                  )}
                  {editAuthMessage && (
                    <p style={{ marginTop: "0.5rem", color: "#444" }}>
                      {editAuthMessage}
                    </p>
                  )}
                  {editPopupBlocked && editAuthUrl && (
                    <p style={{ marginTop: "0.5rem", color: "#b45309" }}>
                      Pop-up blocked. {" "}
                      <a href={editAuthUrl} target="_blank" rel="noreferrer">
                        Open the Google authorization flow
                      </a>
                      .
                    </p>
                  )}
                  {editAuthError && (
                    <p style={{ marginTop: "0.5rem", color: "red" }}>
                      {editAuthError}
                    </p>
                  )}
                </div>
                <div
                  style={{
                    marginTop: "1rem",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button type="submit" disabled={editModalSaving}>
                    {editModalSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={closeEditModal}
                    disabled={editModalSaving}
                    style={{ marginLeft: "0.5rem" }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
      </div>
      <GA4OvernightAuditDialog
        isOpen={overnightDialogOpen}
        onClose={() => setOvernightDialogOpen(false)}
        organizationId={selectedOrgId}
      />
    </>
  )
}

function sanitizePageLocationMatch(match) {
  if (!match || typeof match !== "object") return null
  const type = typeof match.type === "string" ? match.type.toLowerCase() : ""
  if (!type) return null
  const parameter =
    typeof match.parameter === "string" && match.parameter
      ? match.parameter
      : null
  const rawValue =
    typeof match.value === "string"
      ? match.value
      : typeof match.value === "number"
        ? String(match.value)
        : ""
  let value = rawValue.trim()
  if (value.length > 250) value = `${value.slice(0, 247)}…`
  return {
    type,
    parameter,
    value: value || null,
  }
}

function normalizeEventCount(value) {
  if (value === null || value === undefined) return null
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value)
  }
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

function buildCcrCarCalculationSourceRows(ga4PropertyId, rows) {
  if (!Array.isArray(rows) || !ga4PropertyId) return []
  return rows.flatMap((row) => {
    const itemBreakdown = Array.isArray(row?.itemBreakdown)
      ? row.itemBreakdown
      : Array.isArray(row?.item_breakdown)
        ? row.item_breakdown
        : []
    const eventOwner =
      typeof row?.eventOwner === "string" ? row.eventOwner : null
    const productName =
      typeof row?.productName === "string" ? row.productName : null
    const eventAction =
      typeof row?.eventAction === "string" ? row.eventAction : null
    const elementText =
      typeof row?.elementText === "string" ? row.elementText : null

    return itemBreakdown.map((entry) => ({
      ga4_property_id: ga4PropertyId,
      event_owner: eventOwner,
      product_name: productName,
      event_action: eventAction,
      element_text: elementText,
      item_id: typeof entry?.item_id === "string" ? entry.item_id : null,
      page_type: typeof entry?.page_type === "string" ? entry.page_type : null,
      event_count: normalizeEventCount(entry?.event_count) ?? 0,
      asc_cta_count: normalizeEventCount(entry?.asc_cta_count) ?? 0,
      asc_form_submission_count:
        normalizeEventCount(entry?.asc_form_submission_count) ?? 0,
      item_make: typeof entry?.item_make === "string" ? entry.item_make : null,
      item_model: typeof entry?.item_model === "string" ? entry.item_model : null,
      item_type: typeof entry?.item_type === "string" ? entry.item_type : null,
      item_year: typeof entry?.item_year === "string" ? entry.item_year : null,
      item_condition:
        typeof entry?.item_condition === "string" ? entry.item_condition : null,
    }))
  })
}
